import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import {
  FiBriefcase,
  FiCheckCircle,
  FiCpu,
  FiImage,
  FiMail,
  FiPlusCircle,
  FiSave,
  FiSettings,
  FiShield,
  FiTrendingUp,
  FiUsers,
  FiTrash2,
} from 'react-icons/fi';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import PasswordInput from '../components/ui/PasswordInput';
import { MODULOS, FEATURE_FLAGS } from '../config/modulos';
import { getLandingPreviewData, reordenarBloquesLanding } from '../utils/landingEditor';

const TIPOS_NEGOCIO = [
  { value: 'general', label: 'General' },
  { value: 'taller', label: 'Taller mecanico' },
  { value: 'reforma', label: 'Reformas / albanileria' },
  { value: 'electricista', label: 'Pymes electricistas' },
  { value: 'restaurante', label: 'Restaurante' },
];

const PLANES = [
  { value: 'starter', label: 'Starter' },
  { value: 'pro', label: 'Pro' },
  { value: 'premium', label: 'Premium' },
  { value: 'custom', label: 'Custom' },
];

const empresaNuevaInicial = {
  nombre: '',
  cif: '',
  direccion: '',
  email: '',
  telefono: '',
  tipoNegocio: 'general',
  plan: 'starter',
};

const trabajadorNuevoInicial = {
  nombre: '',
  apellidos: '',
  email: '',
  telefono: '',
  puesto: '',
  password: '',
  rol: 'empleado',
};

const LIMITES = {
  plantillaSlack: 2000,
  landingTitulo: 120,
  landingSlogan: 140,
  landingDescripcion: 1200,
  landingCtaTexto: 60,
  landingCtaLink: 300,
  landingEmail: 120,
  landingTelefono: 40,
  landingWhatsapp: 40,
  landingServiciosItems: 30,
  landingServiciosItemLongitud: 120,
  landingBeneficiosItems: 30,
  landingBeneficiosItemLongitud: 180,
};

const PLANTILLA_SLACK_POR_DEFECTO = '👤 *Nuevo trabajador registrado*\n• Empresa: {{empresa}}\n• Usuario: {{usuario}}\n• Email: {{email}}\n• Rol: {{rol}}\n• Alta realizada por: {{actor}}';

const PROMPTS_POR_DEFECTO = {
  presupuestos: 'Extraes líneas de presupuesto (talleres/construcción) de un texto en español. Devuelve solo JSON: {"lineas":[{"descripcion":"","cantidad":0,"precioUnitario":0,"descuento":0,"iva":21}]}. No inventes datos no dichos: usa 0 si falta cantidad o precio. Sin markdown ni explicaciones.',
  almacen: 'Extraes productos para el catálogo de inventario de almacén a partir de un texto o documento. Devuelve solo un array JSON: [{"nombre":"","descripcion":"","sku":"","categoria":"","unidad":"ud","stock":0,"stockMinimo":0,"precio":0,"precioCompra":0,"iva":21}]. Sin markdown ni explicaciones.',
  clientes: 'Extraes datos de cliente de una transcripción en español. Solo datos afirmados explícitamente; nunca deduzcas. Devuelve solo JSON: {"datos":{"nombre":null,"apellidos":null,"cif":null,"email":null,"telefono":null,"direccion":null,"poblacion":null,"notas":null},"camposDetectados":[],"avisos":[]}. Sin markdown.',
  presupuestos_faltantes: 'Eres un experto revisor de presupuestos técnicos. Analizas las líneas del presupuesto enviadas y sugieres partidas, materiales o mano de obra que habitualmente se requieren pero no se han incluido. Devuelve solo un objeto JSON: {"avisos":[],"lineasSugeridas":[]}. Sin markdown.',
  tareas: 'Extraes la información principal de una tarea a partir de una transcripción en español. Devuelve solo JSON: {"datos":{"titulo":null,"descripcion":null,"urgencia":"media","estado":"pendiente","fechaTope":null,"diasPreaviso":null,"asignadoNombre":null,"clienteNombre":null},"camposDetectados":[],"avisos":[]}. Usa fechas ISO YYYY-MM-DD cuando se mencionen; no inventes datos. Sin markdown.',
};

function normalizarReintentosIA(valor) {
  const numero = Number(valor);
  return Number.isFinite(numero) ? Math.min(5, Math.max(0, Math.trunc(numero))) : 0;
}

function prepararEmpresaParaIA(empresa) {
  if (!empresa) return empresa;
  const ia = empresa.ia || {};
  const presupuestos = ia.presupuestos || {};
  const almacen = ia.almacen || {};
  const clientes = ia.clientes || {};
  const presupuestos_faltantes = ia.presupuestos_faltantes || {};
  const tareas = ia.tareas || {};

  return {
    ...empresa,
    ia: {
      ...ia,
      presupuestos: {
        activa: true,
        proveedor: 'local',
        prompt: presupuestos.prompt || '',
        ...presupuestos,
      },
      almacen: {
        activa: true,
        proveedor: 'local',
        prompt: almacen.prompt || '',
        ...almacen,
      },
      clientes: {
        activa: true,
        proveedor: 'groq',
        prompt: clientes.prompt || '',
        ...clientes,
      },
      presupuestos_faltantes: {
        activa: true,
        proveedor: 'groq',
        prompt: presupuestos_faltantes.prompt || '',
        ...presupuestos_faltantes,
      },
      tareas: {
        activa: true,
        proveedor: 'groq',
        prompt: tareas.prompt || '',
        ...tareas,
      },
      catalogo: (Array.isArray(ia.catalogo) && ia.catalogo.length > 0 ? ia.catalogo : [
        { clave: 'presupuestos', nombre: 'Groq Presupuestos', activo: presupuestos.activa !== false, orden: 1, proveedor: 'groq', modeloChat: 'llama-3.3-70b-versatile', modeloTranscripcion: 'whisper-large-v3', apiKey: '', prompt: presupuestos.prompt || '' },
        { clave: 'presupuestos', nombre: 'Gemini Presupuestos', activo: true, orden: 2, proveedor: 'gemini', modeloChat: 'gemini-2.0-flash', modeloTranscripcion: '', apiKey: '', prompt: '' },
        { clave: 'clientes', nombre: 'Groq Clientes', activo: true, orden: 1, proveedor: 'groq', modeloChat: 'llama-3.3-70b-versatile', modeloTranscripcion: 'whisper-large-v3', apiKey: '', prompt: clientes.prompt || '' },
        { clave: 'almacen', nombre: 'Groq Almacén', activo: almacen.activa !== false, orden: 1, proveedor: 'groq', modeloChat: 'llama-3.3-70b-versatile', modeloTranscripcion: 'whisper-large-v3', apiKey: '', prompt: almacen.prompt || '' },
        { clave: 'presupuestos_faltantes', nombre: 'Groq Revisión y Faltantes', activo: true, orden: 1, proveedor: 'groq', modeloChat: 'llama-3.3-70b-versatile', modeloTranscripcion: 'whisper-large-v3', apiKey: '', prompt: presupuestos_faltantes.prompt || '' },
        { clave: 'tareas', nombre: 'Groq Tareas', activo: true, orden: 1, proveedor: 'groq', modeloChat: 'llama-3.3-70b-versatile', modeloTranscripcion: 'whisper-large-v3', apiKey: '', prompt: tareas.prompt || '' },
      ]).map((item) => ({ ...item, reintentos: normalizarReintentosIA(item.reintentos) })),
    },
  };
}

function toArrayDesdeTexto(valor) {
  if (typeof valor !== 'string') return [];
  return valor
    .split('\n')
    .map((item) => item.trim())
    .filter(Boolean);
}

function normalizarListaLanding(valor) {
  if (Array.isArray(valor)) return valor.filter((item) => item !== null && item !== undefined).map((item) => String(item));
  if (typeof valor === 'string') return toArrayDesdeTexto(valor);
  return [];
}

function crearBloqueLanding(index = 0) {
  return {
    id: `bloque-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    activo: true,
    tipo: 'texto',
    titulo: '',
    contenido: '',
    altura: 280,
    orden: index,
  };
}

function esImagenValidaUrlOBase64(valor) {
  const v = String(valor || '').trim();
  if (!v) return true;
  if (v.startsWith('data:image/')) return true;
  return /^https?:\/\//i.test(v);
}

function renderPlantilla(plantilla, vars = {}) {
  return String(plantilla || '').replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) => String(vars[key] ?? ''));
}

function VistaPreviaPlantillaGlobal({ plantilla, nombreEmpresa = 'Empresa' }) {
  if (!plantilla) return null;

  const titulo = renderPlantilla(String(plantilla?.titulo || '{{nombre}}'), { nombre: nombreEmpresa });
  const slogan = String(plantilla?.slogan || 'Tu presencia digital').trim();
  const descripcion = String(plantilla?.descripcionLanding || plantilla?.descripcion || '').trim();
  const ctaTexto = String(plantilla?.ctaTexto || 'Solicitar demo').trim();
  const servicios = normalizarListaLanding(plantilla?.servicios).length ? normalizarListaLanding(plantilla?.servicios) : ['Servicio principal'];
  const beneficios = normalizarListaLanding(plantilla?.beneficios).length ? normalizarListaLanding(plantilla?.beneficios) : ['Más confianza'];
  const stats = normalizarListaLanding(plantilla?.stats).length ? normalizarListaLanding(plantilla?.stats) : ['Marca sólida'];
  const testimonials = normalizarListaLanding(plantilla?.testimonials).length ? normalizarListaLanding(plantilla?.testimonials) : ['“La experiencia marca la diferencia”'];
  const componentesActivos = Array.isArray(plantilla?.componentes) && plantilla.componentes.length ? plantilla.componentes : ['hero', 'servicios', 'beneficios', 'stats'];
  const colorPrimario = String(plantilla?.colorPrimario || '#4f46e5');
  const colorSecundario = String(plantilla?.colorSecundario || '#0ea5e9');

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 18, overflow: 'hidden', background: '#fff', boxShadow: '0 12px 30px rgba(15,23,42,0.06)' }}>
      <div style={{ background: `linear-gradient(135deg, ${colorPrimario}, ${colorSecundario})`, color: '#fff', padding: '20px 18px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 10 }}>
          <div style={{ fontWeight: 700, letterSpacing: 1 }}>{plantilla?.nombre || 'Plantilla'}</div>
          <span style={{ fontSize: 11, background: 'rgba(255,255,255,0.16)', padding: '6px 10px', borderRadius: 999 }}>{plantilla?.heroBadge || 'Plantilla'}</span>
        </div>
        <h3 style={{ margin: '0 0 8px', fontSize: 30, lineHeight: 1.1 }}>{titulo}</h3>
        <p style={{ margin: 0, opacity: 0.95, fontSize: 14 }}>{slogan}</p>
        <div style={{ marginTop: 16, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button type="button" style={{ background: '#fff', color: colorPrimario, border: 'none', borderRadius: 999, padding: '10px 16px', fontWeight: 700 }}>{ctaTexto}</button>
          <button type="button" style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.5)', color: '#fff', borderRadius: 999, padding: '10px 16px', fontWeight: 700 }}>Ver más</button>
        </div>
      </div>

      <div style={{ padding: 18, display: 'grid', gap: 14 }}>
        {componentesActivos.includes('stats') ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
            {stats.map((item, index) => (
              <div key={`${item}-${index}`} style={{ background: '#f8fafc', border: '1px solid var(--border)', borderRadius: 12, padding: 12 }}>
                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 6 }}>Estadística</div>
                <strong style={{ fontSize: 15 }}>{item}</strong>
              </div>
            ))}
          </div>
        ) : null}

        {componentesActivos.includes('servicios') ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
            {servicios.map((item, index) => (
              <div key={`${item}-${index}`} style={{ border: '1px solid var(--border)', borderRadius: 12, padding: 14, background: '#fff' }}>
                <div style={{ width: 38, height: 38, borderRadius: 10, background: `${colorPrimario}14`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: colorPrimario, fontWeight: 700, marginBottom: 10 }}>0{index + 1}</div>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>{item}</div>
                <div style={{ color: '#64748b', fontSize: 12 }}>{descripcion || 'Contenido clave para captar clientes y generar confianza.'}</div>
              </div>
            ))}
          </div>
        ) : null}

        {componentesActivos.includes('beneficios') ? (
          <div style={{ display: 'grid', gap: 8 }}>
            {beneficios.map((item, index) => (
              <div key={`${item}-${index}`} style={{ display: 'flex', alignItems: 'center', gap: 10, border: '1px solid var(--border)', borderRadius: 10, padding: '10px 12px', background: '#f8fafc' }}>
                <span style={{ width: 8, height: 8, background: colorPrimario, borderRadius: '50%' }} />
                <span>{item}</span>
              </div>
            ))}
          </div>
        ) : null}

        {componentesActivos.includes('testimonials') ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 12 }}>
            {testimonials.map((item, index) => (
              <div key={`${item}-${index}`} style={{ border: '1px solid var(--border)', borderRadius: 12, padding: 12, background: '#f8fafc', fontStyle: 'italic' }}>
                {item}
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function obtenerPlantillaLanding(tipo = 'clasico', empresaNombre = 'Empresa') {
  const nombre = String(empresaNombre || 'Empresa').trim() || 'Empresa';
  switch (tipo) {
    case 'luxury':
      return {
        templateId: 'luxury',
        templateName: 'Luxury Brand',
        titulo: `${nombre}`,
        slogan: 'Elegancia, confianza y servicio premium',
        descripcion: 'Una presencia digital premium para transmitir calidad, confianza y una experiencia impecable desde el primer contacto.',
        ctaTexto: 'Contactar',
        ctaLink: '/login',
        heroBadge: 'Marca premium',
        layoutVariant: 'moderno',
        colorPrimario: '#0f172a',
        colorSecundario: '#c4a46a',
        servicios: ['Atención personalizada', 'Experiencia premium', 'Comunicación cuidada'],
        beneficios: ['Más confianza', 'Imagen distinguida', 'Atención más cercana'],
        stats: ['Servicio premium', 'Marca diferenciada', 'Cliente satisfecho'],
        testimonials: ['“Cada detalle transmite calidad”'],
        bloques: [],
      };
    case 'moderno':
      return {
        templateId: 'moderno',
        templateName: 'Moderna',
        titulo: `Potencia digital para ${nombre}`,
        slogan: 'Más ventas, menos fricción',
        descripcion: 'Convierte visitas en oportunidades con una experiencia clara, rápida y profesional.',
        ctaTexto: 'Solicitar demo',
        ctaLink: '/login',
        heroBadge: 'Diseño pensado para vender',
        layoutVariant: 'moderno',
        servicios: ['Automatización de procesos', 'Panel de operaciones', 'Seguimiento comercial'],
        beneficios: ['Más visibilidad', 'Menos tareas manuales', 'Mejor seguimiento'],
        stats: ['+40% de productividad', 'Tiempo de respuesta + rápido', 'Gestión centralizada'],
        testimonials: ['“Trix nos cambió la forma de trabajar”'],
        bloques: [],
      };
    case 'minimalista':
      return {
        templateId: 'minimalista',
        templateName: 'Minimalista',
        titulo: `Gestión simple y eficaz para ${nombre}`,
        slogan: 'Menos ruido, mejores resultados',
        descripcion: 'Una landing limpia, directa y preparada para captar clientes.',
        ctaTexto: 'Hablar con nosotros',
        ctaLink: '/login',
        heroBadge: 'Minimalista y claro',
        layoutVariant: 'minimalista',
        servicios: ['Organización diaria', 'Herramientas de seguimiento', 'Atención más rápida'],
        beneficios: ['Más claridad', 'Menos complejidad', 'Mejor enfoque'],
        stats: ['Setup rápido', 'Sin sobrecarga', 'Mismo día de uso'],
        testimonials: ['“Muy fácil de adaptar”'],
        bloques: [],
      };
    case 'clasico':
    default:
      return {
        templateId: 'clasico',
        templateName: 'Clásica',
        titulo: `Gestión digital para ${nombre}`,
        slogan: 'Más control, menos caos',
        descripcion: 'Trix centraliza clientes, ventas, tareas y operaciones en un único espacio.',
        ctaTexto: 'Solicitar demo',
        ctaLink: '/login',
        heroBadge: 'Gestión conectada para PYMEs',
        layoutVariant: 'clasico',
        servicios: ['Clientes', 'Citas', 'Facturas', 'Stock'],
        beneficios: ['Más organización', 'Menos errores', 'Más tiempo para crecer'],
        stats: ['+1.000 tareas gestionadas', '100% de visibilidad', 'Más control diario'],
        testimonials: ['“Todo está mucho más ordenado”'],
        bloques: [],
      };
  }
}

function validarLandingLongitudes(landing = {}) {
  const servicios = normalizarListaLanding(landing.servicios);
  const beneficios = normalizarListaLanding(landing.beneficios);

  if (String(landing.titulo || '').length > LIMITES.landingTitulo) return `El titulo de landing supera ${LIMITES.landingTitulo} caracteres.`;
  if (String(landing.slogan || '').length > LIMITES.landingSlogan) return `El slogan de landing supera ${LIMITES.landingSlogan} caracteres.`;
  if (String(landing.descripcion || '').length > LIMITES.landingDescripcion) return `La descripcion de landing supera ${LIMITES.landingDescripcion} caracteres.`;
  if (String(landing.ctaTexto || '').length > LIMITES.landingCtaTexto) return `El texto CTA supera ${LIMITES.landingCtaTexto} caracteres.`;
  if (String(landing.ctaLink || '').length > LIMITES.landingCtaLink) return `El enlace CTA supera ${LIMITES.landingCtaLink} caracteres.`;
  if (String(landing.email || '').length > LIMITES.landingEmail) return `El email de landing supera ${LIMITES.landingEmail} caracteres.`;
  if (String(landing.telefono || '').length > LIMITES.landingTelefono) return `El telefono de landing supera ${LIMITES.landingTelefono} caracteres.`;
  if (String(landing.whatsapp || '').length > LIMITES.landingWhatsapp) return `El WhatsApp de landing supera ${LIMITES.landingWhatsapp} caracteres.`;
  if (servicios.length > LIMITES.landingServiciosItems) return `No puedes guardar mas de ${LIMITES.landingServiciosItems} servicios.`;
  if (beneficios.length > LIMITES.landingBeneficiosItems) return `No puedes guardar mas de ${LIMITES.landingBeneficiosItems} beneficios.`;
  if (servicios.some((s) => String(s || '').length > LIMITES.landingServiciosItemLongitud)) return `Cada servicio debe tener maximo ${LIMITES.landingServiciosItemLongitud} caracteres.`;
  if (beneficios.some((s) => String(s || '').length > LIMITES.landingBeneficiosItemLongitud)) return `Cada beneficio debe tener maximo ${LIMITES.landingBeneficiosItemLongitud} caracteres.`;
  return '';
}

async function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

async function comprimirImagen(file, { maxWidth = 1200, maxHeight = 1200, quality = 0.8 } = {}) {
  const original = await fileToDataUrl(file);
  const img = await new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = original;
  });

  const ratio = Math.min(maxWidth / img.width, maxHeight / img.height, 1);
  const width = Math.max(1, Math.round(img.width * ratio));
  const height = Math.max(1, Math.round(img.height * ratio));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, width, height);
  return canvas.toDataURL('image/jpeg', quality);
}

function normalizarEmpresaParaGuardar(empresaActual, esSuperadmin, configSlack = {}, incluirSlack = false) {
  const payload = {
    nombre: empresaActual.nombre || '',
    cif: empresaActual.cif || '',
    direccion: empresaActual.direccion || '',
    email: empresaActual.email || '',
    telefono: empresaActual.telefono || '',
    logo: empresaActual.logo || '',
    observacionDefectoPresupuesto: empresaActual.observacionDefectoPresupuesto || empresaActual.observacionDefecto || '',
    observacionDefectoFactura: empresaActual.observacionDefectoFactura || empresaActual.observacionDefecto || '',
    slackPlantillaAltaTrabajador: empresaActual.slackPlantillaAltaTrabajador || '',
    landingPage: empresaActual.landingPage || {},
  };

  if (incluirSlack) {
    payload.notificaciones = {
      slack: {
        activo: Boolean(configSlack.activo),
        canal: configSlack.canal || '',
        eventos: configSlack.eventos || {},
      },
    };
  }

  if (esSuperadmin) {
    payload.tipoNegocio = empresaActual.tipoNegocio || 'general';
    payload.plan = empresaActual.plan || 'starter';
    payload.activa = empresaActual.activa !== false;
    payload.itvCreditos = Number(empresaActual.itvCreditos) || 0;
    payload.slackWebhookUrl = empresaActual.slackWebhookUrl || '';
    payload.slackCanal = empresaActual.slackCanal || '';
    if (incluirSlack) payload.notificaciones.slack.webhookUrl = configSlack.webhookUrl || '';
    payload.correoSaliente = empresaActual.correoSaliente || '';
    payload.correoSalientePassword = empresaActual.correoSalientePassword || '';
    payload.smtpHost = empresaActual.smtpHost || '';
    payload.smtpPort = empresaActual.smtpPort !== undefined && empresaActual.smtpPort !== null && empresaActual.smtpPort !== '' ? Number(empresaActual.smtpPort) : null;
    payload.smtpSecure = Boolean(empresaActual.smtpSecure);
    const idsBorrados = Array.isArray(empresaActual.ia?.eliminarCatalogoIds)
      ? empresaActual.ia.eliminarCatalogoIds.map((id) => String(id || '').trim()).filter(Boolean)
      : [];
    payload.ia = {
      groqApiKey: empresaActual.ia?.groqApiKey || '',
      catalogo: Array.isArray(empresaActual.ia?.catalogo)
        ? empresaActual.ia.catalogo.map((item) => ({ ...item, reintentos: normalizarReintentosIA(item.reintentos) }))
        : [],
      eliminarCatalogoIds: idsBorrados,
      presupuestos: empresaActual.ia?.presupuestos || {},
      almacen: empresaActual.ia?.almacen || {},
      clientes: empresaActual.ia?.clientes || {},
      presupuestos_faltantes: empresaActual.ia?.presupuestos_faltantes || {},
    };
  } else {
    payload.slackWebhookUrl = empresaActual.slackWebhookUrl || '';
  }

  return payload;
}

function Seccion({ title, subtitle, icon, children, defaultOpen = true }) {
  return (
    <div className="table-wrap" style={{ marginBottom: 20 }}>
      <details open={defaultOpen} style={{ border: 'none' }}>
        <summary style={{
          listStyle: 'none',
          cursor: 'pointer',
          padding: 16,
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}>
          <strong>{icon} {title}</strong>
          {subtitle ? <span className="muted">{subtitle}</span> : null}
        </summary>
        <div style={{ padding: 16 }}>{children}</div>
      </details>
    </div>
  );
}

function VistaPreviaBloque({ bloque, colorPrimario = '#4f46e5' }) {
  const tipo = bloque?.tipo || 'texto';
  const titulo = String(bloque?.titulo || '').trim();
  const contenido = String(bloque?.contenido || '').trim();

  return (
    <div style={{ marginTop: 12, border: '1px dashed var(--border)', borderRadius: 12, padding: 12, background: '#fff' }}>
      <div className="muted" style={{ marginBottom: 8 }}>Vista previa</div>
      <div style={{ background: '#f8fafc', borderRadius: 12, padding: 14, minHeight: 86 }}>
        {titulo ? <div style={{ fontWeight: 700, marginBottom: 8 }}>{titulo}</div> : null}
        {tipo === 'cta' ? (
          <a href={contenido || '#'} style={{ display: 'inline-block', background: colorPrimario, color: '#fff', padding: '10px 14px', borderRadius: 10, textDecoration: 'none' }}>
            {titulo || 'Ver más'}
          </a>
        ) : null}
        {tipo === 'iframe' ? (
          <div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 12, color: '#64748b', minHeight: Math.max(80, Number(bloque?.altura) || 280) / 3 }}>
            {contenido ? 'Iframe listo para mostrarse en la landing' : 'Añade una URL de iframe para ver la vista previa'}
          </div>
        ) : null}
        {tipo === 'html' && contenido ? <div dangerouslySetInnerHTML={{ __html: contenido }} /> : null}
        {tipo === 'texto' ? <p style={{ margin: 0, color: '#334155', lineHeight: 1.6 }}>{contenido || 'Texto de ejemplo...'}</p> : null}
      </div>
    </div>
  );
}

function VistaPreviaLandingVisual({ empresaActual, ampliada = false }) {
  const preview = getLandingPreviewData(empresaActual?.landingPage || {}, empresaActual || {});
  const colorPrimario = preview.colorPrimario || '#4f46e5';
  const colorSecundario = preview.colorSecundario || '#0ea5e9';
  const heroImage = preview.heroImage || '';
  const logoCabecera = preview.logo || empresaActual?.logo || '';
  const titulo = preview.titulo || 'Gestión digital para tu negocio';
  const slogan = preview.slogan || 'Más control, menos caos';
  const descripcion = preview.descripcion || 'Trix centraliza clientes, ventas, tareas y operaciones en un solo lugar.';
  const ctaTexto = preview.ctaTexto || 'Solicitar demo';
  const servicios = preview.servicios || [];
  const beneficios = preview.beneficios || [];
  const stats = preview.stats || [];
  const testimonials = preview.testimonials || [];

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 16, padding: 14, background: '#fff', boxShadow: '0 10px 30px rgba(15,23,42,0.06)', maxWidth: ampliada ? 900 : '100%', margin: '0 auto' }}>
      <div className="muted" style={{ marginBottom: 10 }}>Vista visual previa en vivo</div>
      <div style={{
        background: heroImage
          ? `linear-gradient(135deg, ${colorPrimario}, ${colorSecundario}), url(${heroImage}) center/cover no-repeat`
          : `linear-gradient(135deg, ${colorPrimario}, ${colorSecundario})`,
        color: '#fff',
        borderRadius: 14,
        padding: 16,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          {logoCabecera ? <img src={logoCabecera} alt={empresaActual?.nombre || 'Logo'} style={{ width: 42, height: 42, borderRadius: 10, objectFit: 'cover', background: '#fff' }} /> : null}
          <div style={{ fontWeight: 700 }}>{empresaActual?.nombre || 'Empresa'}</div>
        </div>
        <div style={{ display: 'inline-block', background: 'rgba(255,255,255,0.16)', borderRadius: 999, padding: '6px 10px', fontSize: 12, marginBottom: 10 }}>{preview.heroBadge || slogan}</div>
        <h3 style={{ margin: '0 0 8px', fontSize: 20 }}>{titulo}</h3>
        <p style={{ margin: 0, lineHeight: 1.5, opacity: 0.95 }}>{descripcion}</p>
        <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ background: '#fff', color: colorPrimario, borderRadius: 999, padding: '8px 12px', fontWeight: 700 }}>{ctaTexto}</span>
          {preview.whatsapp ? <span style={{ border: '1px solid rgba(255,255,255,0.35)', borderRadius: 999, padding: '8px 12px' }}>WhatsApp</span> : null}
        </div>
      </div>
      <div style={{ display: 'grid', gap: 8, marginTop: 10 }}>
        {servicios.length > 0 ? (
          <div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 10 }}>
            <div className="muted" style={{ marginBottom: 6 }}>Servicios</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {servicios.slice(0, 4).map((item, index) => <span key={`${item}-${index}`} style={{ background: '#f1f5f9', borderRadius: 999, padding: '4px 8px', fontSize: 12 }}>{item}</span>)}
            </div>
          </div>
        ) : null}
        {beneficios.length > 0 ? (
          <div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 10 }}>
            <div className="muted" style={{ marginBottom: 6 }}>Beneficios</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {beneficios.slice(0, 4).map((item, index) => <span key={`${item}-${index}`} style={{ background: '#eef2ff', borderRadius: 999, padding: '4px 8px', fontSize: 12 }}>{item}</span>)}
            </div>
          </div>
        ) : null}
        {stats.length > 0 ? (
          <div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 10 }}>
            <div className="muted" style={{ marginBottom: 6 }}>Indicadores</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(90px, 1fr))', gap: 6 }}>
              {stats.slice(0, 3).map((item, index) => <div key={`${item}-${index}`} style={{ background: '#f8fafc', borderRadius: 8, padding: '8px 10px', fontSize: 12 }}>{item}</div>)}
            </div>
          </div>
        ) : null}
        {testimonials.length > 0 ? (
          <div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 10 }}>
            <div className="muted" style={{ marginBottom: 6 }}>Testimonios</div>
            <div style={{ display: 'grid', gap: 6 }}>
              {testimonials.slice(0, 2).map((item, index) => <div key={`${item}-${index}`} style={{ background: '#fef3c7', borderRadius: 8, padding: '8px 10px', fontSize: 12 }}>{item}</div>)}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default function Configuracion() {
  const { usuario, empresa, empresaActivaId } = useAuth();
  const location = useLocation();
  const esSuperadmin = usuario?.rol === 'superadmin';
  const vistaGlobal = esSuperadmin && location.pathname.endsWith('/configuracion');
  const tabDesdeQuery = new URLSearchParams(location.search).get('tab');
  const empresaIdQuery = new URLSearchParams(location.search).get('empresaId');

  const esAdminEmpresa = usuario?.rol === 'admin';
  const puedeEntrar = esSuperadmin || esAdminEmpresa;

  const [globalConfig, setGlobalConfig] = useState({
    appName: 'TRIX',
    soporteEmail: '',
    soporteTelefono: '',
    idioma: 'es',
    timezone: 'Europe/Madrid',
    alertasSlack: true,
    demoPublicaActiva: true,
    landingTemplates: [
      { id: 'luxury', nombre: 'Luxury Brand', categoria: 'premium', descripcion: 'Diseño premium', layout: 'luxury', layoutVariant: 'moderno', colorPrimario: '#0f172a', colorSecundario: '#c4a46a', heroBadge: 'Marca premium', titulo: '{{nombre}}', slogan: 'Elegancia, confianza y servicio premium', descripcionLanding: 'Una presencia digital premium.', ctaTexto: 'Contactar', ctaLink: '/login', servicios: ['Atención personalizada', 'Experiencia premium', 'Comunicación cuidada'], beneficios: ['Más confianza', 'Imagen distinguida', 'Atención más cercana'], stats: ['Servicio premium', 'Marca diferenciada', 'Cliente satisfecho'], testimonials: ['“Cada detalle transmite calidad”'], componentes: ['hero', 'servicios', 'beneficios', 'stats', 'testimonials'] },
      { id: 'moderno', nombre: 'Moderna', categoria: 'corporativa', descripcion: 'Diseño moderno', layout: 'moderno', layoutVariant: 'moderno', colorPrimario: '#1d4ed8', colorSecundario: '#0ea5e9', heroBadge: 'Diseño pensado para vender', titulo: 'Potencia digital para {{nombre}}', slogan: 'Más ventas, menos fricción', descripcionLanding: 'Convierte visitas en oportunidades.', ctaTexto: 'Solicitar demo', ctaLink: '/login', servicios: ['Automatización de procesos'], beneficios: ['Más visibilidad'], stats: ['+40% de productividad'], testimonials: ['“Trix nos cambió la forma de trabajar”'], componentes: ['hero', 'servicios', 'beneficios', 'stats', 'testimonials'] },
      { id: 'minimalista', nombre: 'Minimalista', categoria: 'minimal', descripcion: 'Diseño claro y sobrio', layout: 'minimalista', layoutVariant: 'minimalista', colorPrimario: '#111827', colorSecundario: '#6b7280', heroBadge: 'Minimalista y claro', titulo: 'Gestión simple y eficaz para {{nombre}}', slogan: 'Menos ruido, mejores resultados', descripcionLanding: 'Una landing limpia, directa.', ctaTexto: 'Hablar con nosotros', ctaLink: '/login', servicios: ['Organización diaria'], beneficios: ['Más claridad'], stats: ['Setup rápido'], testimonials: ['“Muy fácil de adaptar”'], componentes: ['hero', 'servicios', 'beneficios', 'stats'] },
      { id: 'clasico', nombre: 'Clásica', categoria: 'estandar', descripcion: 'Diseño tradicional', layout: 'clasico', layoutVariant: 'clasico', colorPrimario: '#4f46e5', colorSecundario: '#0ea5e9', heroBadge: 'Gestión conectada para PYMEs', titulo: 'Gestión digital para {{nombre}}', slogan: 'Más control, menos caos', descripcionLanding: 'Trix centraliza clientes, ventas, tareas y operaciones.', ctaTexto: 'Solicitar demo', ctaLink: '/login', servicios: ['Clientes', 'Citas', 'Facturas', 'Stock'], beneficios: ['Más organización'], stats: ['+1.000 tareas gestionadas'], testimonials: ['“Todo está mucho más ordenado”'], componentes: ['hero', 'servicios', 'beneficios', 'stats', 'testimonials'] },
    ],
  });

  const [empresas, setEmpresas] = useState([]);
  const [empresaId, setEmpresaId] = useState('');
  const [empresaActual, setEmpresaActual] = useState(null);
  const [nuevaEmpresa, setNuevaEmpresa] = useState(empresaNuevaInicial);

  const [trabajadores, setTrabajadores] = useState([]);
  const [trabajadorNuevo, setTrabajadorNuevo] = useState(trabajadorNuevoInicial);

  const [guardando, setGuardando] = useState(false);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [mensaje, setMensaje] = useState('');

  const [creandoEmpresa, setCreandoEmpresa] = useState(false);
  const [creandoTrabajador, setCreandoTrabajador] = useState(false);

  const [probandoSlack, setProbandoSlack] = useState(false);
  const [probandoCorreo, setProbandoCorreo] = useState(false);
  const [configSlack, setConfigSlack] = useState({ activo: false, webhookUrl: '', canal: '', eventos: {}, catalogo: {} });
  const [slackModificado, setSlackModificado] = useState(false);
  const [cargandoSlack, setCargandoSlack] = useState(false);
  const [correoPruebaDestino, setCorreoPruebaDestino] = useState('');
  const [tabActiva, setTabActiva] = useState('empresa');
  const [cuotasIA, setCuotasIA] = useState(null);
  const [cargandoCuotasIA, setCargandoCuotasIA] = useState(false);
  const [errorCuotasIA, setErrorCuotasIA] = useState('');
  const [mensajeCuotasIA, setMensajeCuotasIA] = useState('');
  const [formCuotaEmpresa, setFormCuotaEmpresa] = useState({ limiteTokensMensual: 0, activa: true });
  const [limitesUsuarioIA, setLimitesUsuarioIA] = useState({});
  const [guardandoCuotaEmpresa, setGuardandoCuotaEmpresa] = useState(false);
  const [guardandoCuotaUsuario, setGuardandoCuotaUsuario] = useState('');
  const [clientesPotenciales, setClientesPotenciales] = useState([]);
  const [cargandoClientesPotenciales, setCargandoClientesPotenciales] = useState(false);
  const [leadConversion, setLeadConversion] = useState(null);
  const [convirtiendoLead, setConvirtiendoLead] = useState(false);
  const [subiendoImagen, setSubiendoImagen] = useState(false);
  const [probandoIA, setProbandoIA] = useState(false);
  const [resultadoPruebaIA, setResultadoPruebaIA] = useState('');
  const [promptPruebaIA, setPromptPruebaIA] = useState('hola');
  const [modoPruebaIA, setModoPruebaIA] = useState('strict');
  const [explorandoModelosGroq, setExplorandoModelosGroq] = useState(false);
  const [resultadosExploracionGroq, setResultadosExploracionGroq] = useState('');
  const [apiKeyExploracionGroq, setApiKeyExploracionGroq] = useState('');
  const [promptsPruebaIA, setPromptsPruebaIA] = useState({});
  const [previewAmpliada, setPreviewAmpliada] = useState(false);
  const [plantillaGlobalActiva, setPlantillaGlobalActiva] = useState('luxury');
  const [historialAjustes, setHistorialAjustes] = useState([]);
  const [historialPaginacion, setHistorialPaginacion] = useState({ pagina: 1, paginas: 1, total: 0 });
  const [historialPagina, setHistorialPagina] = useState(1);
  const [historialDesde, setHistorialDesde] = useState('');
  const [historialHasta, setHistorialHasta] = useState('');

  const modulos = useMemo(() => MODULOS, []);
  const featureEntries = useMemo(() => Object.entries(FEATURE_FLAGS), []);
  const actualizarIA = (indice, campo, valor) => {
    setEmpresaActual((prev) => ({
      ...prev,
      ia: {
        ...(prev?.ia || {}),
        catalogo: (prev?.ia?.catalogo || []).map((item, i) => i === indice ? { ...item, [campo]: valor } : item),
      },
    }));
  };

  const actualizarPromptUsoIA = (uso, promptTexto) => {
    setEmpresaActual((prev) => ({
      ...prev,
      ia: {
        ...(prev?.ia || {}),
        [uso]: {
          ...(prev?.ia?.[uso] || {}),
          prompt: promptTexto,
        },
      },
    }));
  };

  const anadirIA = () => {
    setEmpresaActual((prev) => ({
      ...prev,
      ia: {
        ...(prev?.ia || {}),
        catalogo: [...(prev?.ia?.catalogo || []), { clave: 'presupuestos', nombre: 'Nueva IA', activo: true, orden: (prev?.ia?.catalogo?.length || 0) + 1, reintentos: 0, proveedor: 'groq', modeloChat: 'llama-3.1-8b-instant', modeloTranscripcion: '', apiKey: '' }],
      },
    }));
  };

  const eliminarIA = (item) => {
    if (!item) return;
    if (!window.confirm('¿Eliminar esta IA de la empresa?')) return;
    setEmpresaActual((prev) => ({
      ...prev,
      ia: {
        ...(prev?.ia || {}),
        catalogo: (prev?.ia?.catalogo || []).filter((entrada) => entrada !== item),
        eliminarCatalogoIds: [
          ...(prev?.ia?.eliminarCatalogoIds || []),
          item?._id || item?.id,
        ].filter(Boolean),
      },
    }));
  };

  const clearFeedback = () => {
    setError('');
    setMensaje('');
  };

  const cargarTrabajadores = async (empresaObjetivoId, superadmin) => {
    if (!empresaObjetivoId && superadmin) {
      setTrabajadores([]);
      return;
    }
    try {
      const params = superadmin && empresaObjetivoId ? { empresaId: empresaObjetivoId } : {};
      const { data } = await api.get('/usuarios', { params });
      setTrabajadores(Array.isArray(data) ? data.filter((u) => u.rol !== 'superadmin') : []);
    } catch (errCargaUsuarios) {
      setTrabajadores([]);
      setError(errCargaUsuarios?.response?.data?.error || 'No se pudo cargar el equipo de la empresa seleccionada.');
    }
  };

  const cargarHistorialAjustes = async (empresaObjetivoId, pagina = historialPagina) => {
    if (!empresaObjetivoId) {
      setHistorialAjustes([]);
      setHistorialPaginacion({ pagina: 1, paginas: 1, total: 0 });
      return;
    }
    try {
      const params = { page: pagina, limit: 20 };
      if (historialDesde) params.desde = historialDesde;
      if (historialHasta) params.hasta = historialHasta;
      const { data } = await api.get(`/empresas/${empresaObjetivoId}/historial-ajustes`, { params });
      setHistorialAjustes(Array.isArray(data?.historial) ? data.historial : []);
      setHistorialPaginacion(data?.paginacion || { pagina, paginas: 1, total: 0 });
    } catch (_errHistorial) {
      setHistorialAjustes([]);
      setHistorialPaginacion({ pagina: 1, paginas: 1, total: 0 });
    }
  };

  const cargarConfigSlack = async (empresaObjetivoId = empresaActual?._id || empresaId) => {
    const idEmpresa = String(empresaObjetivoId || '').trim();
    if (!idEmpresa) {
      setConfigSlack({ activo: false, webhookUrl: '', canal: '', eventos: {}, catalogo: {} });
      setSlackModificado(false);
      return;
    }

    try {
      setCargandoSlack(true);
      const { data } = await api.get('/empresas/alertas-slack', {
        params: { empresaId: idEmpresa },
      });
      const slack = data?.slack || {};
      setConfigSlack({
        activo: Boolean(slack.activo),
        webhookUrl: slack.webhookUrl || '',
        canal: slack.canal || '',
        eventos: slack.eventos || {},
        catalogo: slack.catalogo || {},
      });
      setSlackModificado(false);
    } catch (errSlack) {
      setConfigSlack({ activo: false, webhookUrl: '', canal: '', eventos: {}, catalogo: {} });
      setSlackModificado(false);
    } finally {
      setCargandoSlack(false);
    }
  };

  const cargarClientesPotenciales = async () => {
    if (!esSuperadmin) {
      setClientesPotenciales([]);
      return;
    }

    try {
      setCargandoClientesPotenciales(true);
      const { data } = await api.get('/empresas/cliente-potencial');
      setClientesPotenciales(Array.isArray(data) ? data : []);
    } catch (errLeads) {
      setClientesPotenciales([]);
      setError(errLeads?.response?.data?.error || 'No se pudieron cargar los leads de ventas.');
    } finally {
      setCargandoClientesPotenciales(false);
    }
  };

  const cargarInicial = async () => {
    if (!puedeEntrar) {
      setCargando(false);
      return;
    }

    clearFeedback();
    setCargando(true);
    try {
      if (esSuperadmin && vistaGlobal) {
        const { data } = await api.get('/configuracion-global');
        setGlobalConfig((prev) => ({ ...prev, ...(data || {}) }));
      } else if (esSuperadmin) {
        const [respGlobal, respEmpresas] = await Promise.all([
          api.get('/configuracion-global'),
          api.get('/empresas'),
        ]);

        setGlobalConfig((prev) => ({ ...prev, ...(respGlobal?.data || {}) }));
        const lista = Array.isArray(respEmpresas?.data) ? respEmpresas.data : [];
        setEmpresas(lista);

        const preferida = lista.find((e) => String(e._id) === String(empresaIdQuery || empresaActivaId)) || lista[0] || null;
        if (preferida) {
          const idPreferida = String(preferida._id);
          setEmpresaId(idPreferida);
          setEmpresaActual(prepararEmpresaParaIA(preferida));
          await cargarTrabajadores(idPreferida, true);
          await cargarHistorialAjustes(idPreferida);
          await cargarConfigSlack(idPreferida);
        } else {
          setEmpresaId('');
          setEmpresaActual(null);
          setTrabajadores([]);
          setHistorialAjustes([]);
        }
      } else if (!vistaGlobal) {
        const { data } = await api.get('/auth/me');
        const empresaPropia = data?.empresa || empresa || null;
        if (empresaPropia?._id) {
          const idEmpresaPropia = String(empresaPropia._id);
          setEmpresaId(idEmpresaPropia);
          setEmpresaActual(prepararEmpresaParaIA(empresaPropia));
          await cargarTrabajadores(idEmpresaPropia, false);
          await cargarHistorialAjustes(idEmpresaPropia);
          await cargarConfigSlack(idEmpresaPropia);
        }
      }
    } catch (errCarga) {
      setError(errCarga?.response?.data?.error || 'No se pudo cargar la configuracion.');
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    if (Array.isArray(globalConfig?.landingTemplates) && globalConfig.landingTemplates.length > 0 && !globalConfig.landingTemplates.some((p) => p?.id === plantillaGlobalActiva)) {
      setPlantillaGlobalActiva(globalConfig.landingTemplates[0].id || 'luxury');
    }
  }, [globalConfig?.landingTemplates, plantillaGlobalActiva]);

  useEffect(() => {
    cargarInicial();
  }, [usuario?._id, esSuperadmin, esAdminEmpresa, vistaGlobal, empresaIdQuery, empresaActivaId]);

  useEffect(() => {
    if (tabDesdeQuery && ['empresa', 'landing', 'equipo', 'comunicaciones', 'ia', 'permisos', 'ventas'].includes(tabDesdeQuery)) {
      setTabActiva(tabDesdeQuery);
    }
  }, [tabDesdeQuery]);

  useEffect(() => {
    if (esSuperadmin && tabActiva === 'ventas') {
      cargarClientesPotenciales();
    }
  }, [esSuperadmin, tabActiva]);

  const cargarCuotasIA = async (empresaId) => {
    if (!empresaId) return;
    setCargandoCuotasIA(true);
    setErrorCuotasIA('');
    try {
      const { data } = await api.get(`/empresas/${empresaId}/ia/cuotas`);
      setCuotasIA(data);
      setFormCuotaEmpresa({
        limiteTokensMensual: Number(data?.empresa?.limiteTokensMensual) || 0,
        activa: data?.empresa?.activa !== false,
      });
      const mapa = {};
      (data?.usuarios || []).forEach((u) => {
        mapa[u._id] = String(Number(u.limiteTokensMensual) || 0);
      });
      setLimitesUsuarioIA(mapa);
    } catch (e) {
      setErrorCuotasIA(e.response?.data?.error || 'Error al cargar las cuotas de IA');
    } finally {
      setCargandoCuotasIA(false);
    }
  };

  useEffect(() => {
    if (tabActiva === 'ia' && empresaActual?._id) {
      cargarCuotasIA(empresaActual._id);
    }  }, [tabActiva, empresaActual?._id]);

  const esEnteroNoNegativo = (valor) => {
    const numero = Number(valor);
    return Number.isFinite(numero) && Number.isInteger(numero) && numero >= 0;
  };

  const guardarCuotaEmpresaIA = async () => {
    setMensajeCuotasIA('');
    setErrorCuotasIA('');
    if (!esEnteroNoNegativo(formCuotaEmpresa.limiteTokensMensual)) {
      setErrorCuotasIA('El límite mensual debe ser un número entero mayor o igual que 0.');
      return;
    }
    setGuardandoCuotaEmpresa(true);
    try {
      await api.put(`/empresas/${empresaActual._id}/ia/cuotas`, {
        limiteTokensMensual: Number(formCuotaEmpresa.limiteTokensMensual),
        activa: Boolean(formCuotaEmpresa.activa),
      });
      await cargarCuotasIA(empresaActual._id);
      setMensajeCuotasIA('Cuota de la empresa guardada correctamente.');
    } catch (e) {
      setErrorCuotasIA(e.response?.data?.error || 'Error al guardar la cuota de la empresa');
    } finally {
      setGuardandoCuotaEmpresa(false);
    }
  };

  const guardarCuotaUsuarioIA = async (usuarioCuota) => {
    setMensajeCuotasIA('');
    setErrorCuotasIA('');
    const valor = limitesUsuarioIA[usuarioCuota._id];
    if (!esEnteroNoNegativo(valor === '' ? 0 : valor)) {
      setErrorCuotasIA('El límite del usuario debe ser un número entero mayor o igual que 0.');
      return;
    }
    setGuardandoCuotaUsuario(usuarioCuota._id);
    try {
      await api.put(`/usuarios/${usuarioCuota._id}/ia/cuota`, {
        limiteTokensMensual: Number(valor === '' ? 0 : valor),
      });
      await cargarCuotasIA(empresaActual._id);
      setMensajeCuotasIA('Límite del usuario guardado correctamente.');
    } catch (e) {
      setErrorCuotasIA(e.response?.data?.error || 'Error al guardar el límite del usuario');
    } finally {
      setGuardandoCuotaUsuario('');
    }
  };

  useEffect(() => {
    if (empresaActual?._id) {
      cargarConfigSlack(empresaActual._id);
    }
  }, [empresaActual?._id]);

  useEffect(() => {
    if (!correoPruebaDestino && usuario?.email) {
      setCorreoPruebaDestino(String(usuario.email));
    }
  }, [correoPruebaDestino, usuario]);

  if (!puedeEntrar) {
    return (
      <div className="page">
        <h1 className="page-title">Configuracion</h1>
        <div className="alert-error">No tienes permisos para acceder a esta vista.</div>
      </div>
    );
  }

  const actualizarEmpresaActual = (campo, valor) => {
    setEmpresaActual((prev) => ({ ...prev, [campo]: valor }));
  };

  const actualizarConfigSlack = (actualizador) => {
    setSlackModificado(true);
    setConfigSlack((prev) => (typeof actualizador === 'function' ? actualizador(prev) : { ...prev, ...actualizador }));
  };

  const actualizarLanding = (campo, valor) => {
    setEmpresaActual((prev) => ({
      ...prev,
      landingPage: {
        ...(prev?.landingPage || {}),
        [campo]: valor,
      },
    }));
  };

  const aplicarPlantillaLanding = (tipo) => {
    const plantillasGlobales = Array.isArray(globalConfig?.landingTemplates) ? globalConfig.landingTemplates : [];
    const plantillaGlobal = plantillasGlobales.find((tpl) => String(tpl?.id || '').toLowerCase() === String(tipo || '').toLowerCase()) || null;
    const plantillaBase = plantillaGlobal || obtenerPlantillaLanding(tipo, empresaActual?.nombre || 'Empresa');
    const plantilla = {
      ...plantillaBase,
      titulo: renderPlantilla(String(plantillaBase?.titulo || 'Gestión digital para {{nombre}}'), { nombre: empresaActual?.nombre || 'Empresa' }),
      slogan: String(plantillaBase?.slogan || '').trim(),
      descripcion: String(plantillaBase?.descripcionLanding || plantillaBase?.descripcion || '').trim(),
      ctaTexto: String(plantillaBase?.ctaTexto || 'Solicitar demo').trim(),
      ctaLink: String(plantillaBase?.ctaLink || '/login').trim(),
      colorPrimario: plantillaBase?.colorPrimario || '#4f46e5',
      colorSecundario: plantillaBase?.colorSecundario || '#0ea5e9',
      servicios: normalizarListaLanding(plantillaBase?.servicios),
      beneficios: normalizarListaLanding(plantillaBase?.beneficios),
      stats: normalizarListaLanding(plantillaBase?.stats),
      testimonials: normalizarListaLanding(plantillaBase?.testimonials),
      componentes: Array.isArray(plantillaBase?.componentes) ? plantillaBase.componentes : ['hero', 'servicios', 'beneficios', 'stats'],
      bloques: Array.isArray(plantillaBase?.bloques) ? plantillaBase.bloques : [],
    };

    setEmpresaActual((prev) => ({
      ...prev,
      landingPage: {
        ...(prev?.landingPage || {}),
        ...plantilla,
        templateId: plantilla?.templateId || plantilla?.id || String(tipo || 'custom'),
        templateName: plantilla?.templateName || plantilla?.nombre || 'Plantilla',
        servicios: normalizarListaLanding(plantilla.servicios),
        beneficios: normalizarListaLanding(plantilla.beneficios),
        stats: normalizarListaLanding(plantilla.stats),
        testimonials: normalizarListaLanding(plantilla.testimonials),
        bloques: Array.isArray(plantilla.bloques) ? plantilla.bloques : [],
      },
    }));
  };

  const guardarPlantillaLandingActual = () => {
    const landingActual = empresaActual?.landingPage || {};
    const plantillas = Array.isArray(landingActual.templates) ? landingActual.templates : [];
    const nombreBase = String(landingActual.templateName || landingActual.titulo || 'Plantilla').trim() || 'Plantilla';
    const nuevaPlantilla = {
      id: `plantilla-${Date.now()}`,
      nombre: `${nombreBase} ${plantillas.length + 1}`,
      datos: {
        ...landingActual,
        servicios: normalizarListaLanding(landingActual.servicios),
        beneficios: normalizarListaLanding(landingActual.beneficios),
        stats: normalizarListaLanding(landingActual.stats),
        testimonials: normalizarListaLanding(landingActual.testimonials),
        bloques: Array.isArray(landingActual.bloques) ? [...landingActual.bloques] : [],
      },
    };

    setEmpresaActual((prev) => ({
      ...prev,
      landingPage: {
        ...(prev?.landingPage || {}),
        templates: [...plantillas, nuevaPlantilla],
        templateId: 'custom',
        templateName: nuevaPlantilla.nombre,
      },
    }));
  };

  const aplicarPlantillaGuardada = (plantilla) => {
    const datos = plantilla?.datos || {};
    setEmpresaActual((prev) => ({
      ...prev,
      landingPage: {
        ...(prev?.landingPage || {}),
        ...datos,
        servicios: normalizarListaLanding(datos.servicios),
        beneficios: normalizarListaLanding(datos.beneficios),
        stats: normalizarListaLanding(datos.stats),
        testimonials: normalizarListaLanding(datos.testimonials),
        bloques: Array.isArray(datos.bloques) ? [...datos.bloques] : [],
        templateId: 'custom',
        templateName: plantilla?.nombre || 'Plantilla guardada',
      },
    }));
  };

  const eliminarPlantillaGuardada = (id) => {
    setEmpresaActual((prev) => ({
      ...prev,
      landingPage: {
        ...(prev?.landingPage || {}),
        templates: (Array.isArray(prev?.landingPage?.templates) ? prev.landingPage.templates : []).filter((plantilla) => plantilla?.id !== id),
      },
    }));
  };

  const onEmpresaSeleccionada = async (nuevoId) => {
    setEmpresaId(nuevoId);
    setHistorialPagina(1);
    const seleccion = empresas.find((e) => String(e._id) === String(nuevoId));
    setEmpresaActual(prepararEmpresaParaIA(seleccion || null));
    clearFeedback();

    if (esSuperadmin) {
      await cargarTrabajadores(nuevoId, true);
      await cargarHistorialAjustes(nuevoId, 1);
      return;
    }

    await cargarTrabajadores(nuevoId, false);
    await cargarHistorialAjustes(nuevoId, 1);
  };

  const aplicarFiltroHistorial = async () => {
    setHistorialPagina(1);
    await cargarHistorialAjustes(String(empresaActual?._id || empresaId), 1);
  };

  const cambiarPaginaHistorial = async (nuevaPagina) => {
    if (nuevaPagina < 1 || nuevaPagina > historialPaginacion.paginas) return;
    setHistorialPagina(nuevaPagina);
    await cargarHistorialAjustes(String(empresaActual?._id || empresaId), nuevaPagina);
  };

  const guardarConfiguracionGlobal = async () => {
    if (!esSuperadmin) return;
    clearFeedback();
    try {
      setGuardando(true);
      const { data } = await api.put('/configuracion-global', globalConfig);
      setGlobalConfig((prev) => ({ ...prev, ...(data || {}) }));
      setMensaje('Ajustes de Trix guardados correctamente.');
    } catch (errGuardarGlobal) {
      setError(errGuardarGlobal?.response?.data?.error || 'No se pudo guardar la configuracion global.');
    } finally {
      setGuardando(false);
    }
  };

  const actualizarListaPlantillaGlobal = (id, campo, index, valor) => {
    setGlobalConfig((prev) => ({
      ...prev,
      landingTemplates: (Array.isArray(prev?.landingTemplates) ? prev.landingTemplates : []).map((plantilla) => {
        if (plantilla?.id !== id) return plantilla;
        const listaActual = normalizarListaLanding(plantilla?.[campo]);
        return { ...plantilla, [campo]: listaActual.map((item, idx) => (idx === index ? String(valor) : item)) };
      }),
    }));
  };

  const anadirItemPlantillaGlobal = (id, campo, valor = 'Nuevo elemento') => {
    setGlobalConfig((prev) => ({
      ...prev,
      landingTemplates: (Array.isArray(prev?.landingTemplates) ? prev.landingTemplates : []).map((plantilla) => {
        if (plantilla?.id !== id) return plantilla;
        const listaActual = normalizarListaLanding(plantilla?.[campo]);
        return { ...plantilla, [campo]: [...listaActual, String(valor)] };
      }),
    }));
  };

  const eliminarItemPlantillaGlobal = (id, campo, index) => {
    setGlobalConfig((prev) => ({
      ...prev,
      landingTemplates: (Array.isArray(prev?.landingTemplates) ? prev.landingTemplates : []).map((plantilla) => {
        if (plantilla?.id !== id) return plantilla;
        const listaActual = normalizarListaLanding(plantilla?.[campo]);
        return { ...plantilla, [campo]: listaActual.filter((_, idx) => idx !== index) };
      }),
    }));
  };

  const toggleComponentePlantillaGlobal = (id, componente) => {
    setGlobalConfig((prev) => ({
      ...prev,
      landingTemplates: (Array.isArray(prev?.landingTemplates) ? prev.landingTemplates : []).map((plantilla) => {
        if (plantilla?.id !== id) return plantilla;
        const actuales = Array.isArray(plantilla?.componentes) ? plantilla.componentes.filter(Boolean) : [];
        const yaExiste = actuales.includes(componente);
        return { ...plantilla, componentes: yaExiste ? actuales.filter((item) => item !== componente) : [...actuales, componente] };
      }),
    }));
  };

  const moverComponentePlantillaGlobal = (id, fromIndex, toIndex) => {
    if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) return;

    setGlobalConfig((prev) => ({
      ...prev,
      landingTemplates: (Array.isArray(prev?.landingTemplates) ? prev.landingTemplates : []).map((plantilla) => {
        if (plantilla?.id !== id) return plantilla;
        const actuales = Array.isArray(plantilla?.componentes) ? plantilla.componentes.filter(Boolean) : [];
        if (actuales.length < 2) return plantilla;
        const copia = [...actuales];
        const [item] = copia.splice(fromIndex, 1);
        copia.splice(toIndex, 0, item);
        return { ...plantilla, componentes: copia };
      }),
    }));
  };

  const crearPlantillaGlobal = () => {
    const nuevaPlantilla = {
      id: `plantilla-${Date.now()}`,
      nombre: `Plantilla ${((globalConfig?.landingTemplates || []).length || 0) + 1}`,
      categoria: 'custom',
      descripcion: 'Plantilla creada por el superusuario.',
      layout: 'custom',
      layoutVariant: 'custom',
      componentes: ['hero', 'servicios', 'beneficios', 'stats'],
      colorPrimario: '#111827',
      colorSecundario: '#d97706',
      heroBadge: 'Nueva plantilla',
      titulo: 'Tu marca para {{nombre}}',
      slogan: 'Nueva presencia digital',
      descripcionLanding: 'Diseño personalizado para tu negocio.',
      ctaTexto: 'Solicitar información',
      ctaLink: '/login',
      servicios: ['Servicio principal', 'Experiencia de marca', 'Soporte continuo'],
      beneficios: ['Más credibilidad', 'Mejor percepción', 'Más confianza'],
      stats: ['Marca sólida', 'Presencia premium', 'Atención cercana'],
      testimonials: ['“La identidad visual transmite confianza”'],
    };

    setGlobalConfig((prev) => ({
      ...prev,
      landingTemplates: [...(Array.isArray(prev?.landingTemplates) ? prev.landingTemplates : []), nuevaPlantilla],
    }));
    setPlantillaGlobalActiva(nuevaPlantilla.id);
  };

  const duplicarPlantillaGlobal = (plantilla) => {
    if (!plantilla) return;
    const copia = {
      ...plantilla,
      id: `plantilla-${Date.now()}`,
      nombre: `${plantilla.nombre || 'Plantilla'} copia`,
    };
    setGlobalConfig((prev) => ({
      ...prev,
      landingTemplates: [...(Array.isArray(prev?.landingTemplates) ? prev.landingTemplates : []), copia],
    }));
    setPlantillaGlobalActiva(copia.id);
  };

  const actualizarPlantillaGlobal = (id, campo, valor) => {
    setGlobalConfig((prev) => ({
      ...prev,
      landingTemplates: (Array.isArray(prev?.landingTemplates) ? prev.landingTemplates : []).map((plantilla) =>
        plantilla?.id === id ? { ...plantilla, [campo]: valor } : plantilla,
      ),
    }));
  };

  const eliminarPlantillaGlobal = (id) => {
    setGlobalConfig((prev) => ({
      ...prev,
      landingTemplates: (Array.isArray(prev?.landingTemplates) ? prev.landingTemplates : []).filter((plantilla) => plantilla?.id !== id),
    }));
    if (plantillaGlobalActiva === id) {
      const siguiente = (Array.isArray(globalConfig?.landingTemplates) ? globalConfig.landingTemplates : []).find((p) => p?.id !== id);
      setPlantillaGlobalActiva(siguiente ? siguiente.id : '');
    }
  };

  const aplicarPlantillaGlobalAEmpresa = (plantilla) => {
    if (!empresaActual || !plantilla) return;
    const plantillaAplicada = {
      ...empresaActual.landingPage,
      templateId: plantilla.id || 'custom',
      templateName: plantilla.nombre || 'Plantilla',
      titulo: renderPlantilla(String(plantilla?.titulo || '{{nombre}}'), { nombre: empresaActual?.nombre || 'Empresa' }),
      slogan: plantilla?.slogan || '',
      descripcion: plantilla?.descripcionLanding || plantilla?.descripcion || '',
      ctaTexto: plantilla?.ctaTexto || 'Solicitar demo',
      ctaLink: plantilla?.ctaLink || '/login',
      colorPrimario: plantilla?.colorPrimario || '#4f46e5',
      colorSecundario: plantilla?.colorSecundario || '#0ea5e9',
      servicios: Array.isArray(plantilla?.servicios) ? plantilla.servicios : [],
      beneficios: Array.isArray(plantilla?.beneficios) ? plantilla.beneficios : [],
      stats: Array.isArray(plantilla?.stats) ? plantilla.stats : [],
      testimonials: Array.isArray(plantilla?.testimonials) ? plantilla.testimonials : [],
      componentes: Array.isArray(plantilla?.componentes) ? plantilla.componentes : ['hero', 'servicios', 'beneficios'],
      bloques: Array.isArray(plantilla?.bloques) ? plantilla.bloques : [],
    };

    setEmpresaActual((prev) => ({
      ...prev,
      landingPage: {
        ...(prev?.landingPage || {}),
        ...plantillaAplicada,
      },
    }));
    setMensaje(`Plantilla "${plantilla?.nombre || 'global'}" aplicada a la empresa.`);
  };

  const iniciarConversionLead = (lead) => {
    setLeadConversion({
      id: lead?._id,
      nombreEmpresa: lead?.empresa || lead?.nombre || '',
      nombreAdmin: lead?.nombre || '',
      email: lead?.email || '',
      password: '',
      tipoNegocio: 'general',
      plan: 'starter',
    });
  };

  const actualizarConversionLead = (campo, valor) => {
    setLeadConversion((prev) => (prev ? { ...prev, [campo]: valor } : prev));
  };

  const convertirLead = async () => {
    if (!leadConversion?.id) return;
    if (!String(leadConversion.nombreEmpresa || '').trim() || !String(leadConversion.email || '').trim() || !String(leadConversion.password || '').trim()) {
      setError('El nombre de empresa, el email de acceso y la contraseña inicial son obligatorios.');
      return;
    }

    clearFeedback();
    try {
      setConvirtiendoLead(true);
      const { data } = await api.post(`/empresas/cliente-potencial/${leadConversion.id}/convertir`, {
        nombreEmpresa: String(leadConversion.nombreEmpresa || '').trim(),
        nombreAdmin: String(leadConversion.nombreAdmin || '').trim(),
        email: String(leadConversion.email || '').trim(),
        password: String(leadConversion.password || '').trim(),
        tipoNegocio: leadConversion.tipoNegocio || 'general',
        plan: leadConversion.plan || 'starter',
      });
      setMensaje(data?.mensaje || 'Lead convertido correctamente.');
      setLeadConversion(null);
      await cargarClientesPotenciales();
    } catch (errConvertir) {
      setError(errConvertir?.response?.data?.error || 'No se pudo convertir el lead.');
    } finally {
      setConvirtiendoLead(false);
    }
  };

  const guardarEmpresa = async () => {
    if (!empresaActual?._id) return;
    clearFeedback();

    const emailEmpresa = String(empresaActual.email || '').trim();
    if (!emailEmpresa || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailEmpresa)) {
      setError('El email de la empresa es obligatorio y debe tener un formato válido.');
      return;
    }

    if (!esImagenValidaUrlOBase64(empresaActual.logo)) {
      setError('El logo de empresa debe ser una URL http(s) o una imagen base64 data:image.');
      return;
    }
    if (!esImagenValidaUrlOBase64(empresaActual?.landingPage?.logo)) {
      setError('El logo de landing debe ser una URL http(s) o una imagen base64 data:image.');
      return;
    }
    if (!esImagenValidaUrlOBase64(empresaActual?.landingPage?.heroImage)) {
      setError('La imagen de cabecera debe ser una URL http(s) o una imagen base64 data:image.');
      return;
    }
    if (String(empresaActual.slackPlantillaAltaTrabajador || '').length > LIMITES.plantillaSlack) {
      setError(`La plantilla de Slack supera ${LIMITES.plantillaSlack} caracteres.`);
      return;
    }
    const errorLanding = validarLandingLongitudes(empresaActual?.landingPage || {});
    if (errorLanding) {
      setError(errorLanding);
      return;
    }

    try {
      setGuardando(true);

      const payload = normalizarEmpresaParaGuardar(empresaActual, esSuperadmin, configSlack, slackModificado);
      await api.put(`/empresas/${empresaActual._id}`, payload);

      if (slackModificado || configSlack.activo || configSlack.webhookUrl || configSlack.canal || Object.keys(configSlack.eventos || {}).length > 0) {
        await api.put('/empresas/alertas-slack', {
          activo: Boolean(configSlack.activo),
          webhookUrl: configSlack.webhookUrl || '',
          canal: configSlack.canal || '',
          eventos: configSlack.eventos || {},
          empresaId: empresaActual._id,
        });
      }

      if (esSuperadmin) {
        await api.patch(`/empresas/${empresaActual._id}/modulos`, { modulos: empresaActual.modulos || {} });
        await api.patch(`/empresas/${empresaActual._id}/feature-flags`, { featureFlags: empresaActual.featureFlags || {} });
      }

      if (esSuperadmin) {
        const { data } = await api.get('/empresas');
        const lista = Array.isArray(data) ? data : [];
        setEmpresas(lista);
        const seleccion = lista.find((e) => String(e._id) === String(empresaActual._id));
        if (seleccion) setEmpresaActual(prepararEmpresaParaIA(seleccion));
      } else {
        const { data } = await api.get('/empresas/' + empresaActual._id);
        setEmpresaActual(prepararEmpresaParaIA(data));
      }

      await cargarConfigSlack();
      await cargarHistorialAjustes(String(empresaActual._id));

      setMensaje('Ajustes de empresa guardados.');
    } catch (errGuardarEmpresa) {
      setError(errGuardarEmpresa?.response?.data?.error || 'No se pudo guardar la configuracion de la empresa.');
    } finally {
      setGuardando(false);
    }
  };

  const crearEmpresa = async () => {
    if (!esSuperadmin) return;
    if (!nuevaEmpresa.nombre.trim()) {
      setError('El nombre de la empresa es obligatorio.');
      return;
    }
    const emailEmpresa = String(nuevaEmpresa.email || '').trim();
    if (!emailEmpresa || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailEmpresa)) {
      setError('El email de la empresa es obligatorio y debe tener un formato válido.');
      return;
    }

    clearFeedback();
    try {
      setCreandoEmpresa(true);
      const { data } = await api.post('/empresas', {
        nombre: nuevaEmpresa.nombre.trim(),
        cif: nuevaEmpresa.cif.trim(),
        direccion: nuevaEmpresa.direccion.trim(),
        email: nuevaEmpresa.email.trim(),
        telefono: nuevaEmpresa.telefono.trim(),
        tipoNegocio: nuevaEmpresa.tipoNegocio,
        plan: nuevaEmpresa.plan,
      });

      const { data: listado } = await api.get('/empresas');
      const lista = Array.isArray(listado) ? listado : [];
      setEmpresas(lista);
      setNuevaEmpresa(empresaNuevaInicial);
      setMensaje('Empresa creada correctamente.');

      if (data?._id) {
        const nuevoId = String(data._id);
        setEmpresaId(nuevoId);
        const seleccion = lista.find((e) => String(e._id) === nuevoId) || data;
        setEmpresaActual(prepararEmpresaParaIA(seleccion));
      }
    } catch (errNuevaEmpresa) {
      setError(errNuevaEmpresa?.response?.data?.error || 'No se pudo crear la empresa.');
    } finally {
      setCreandoEmpresa(false);
    }
  };

  const eliminarEmpresa = async (empId, empNombre) => {
    if (!esSuperadmin || !empId) return;
    const confirmado = window.confirm(
      `⚠️ ¿Estás seguro de que deseas eliminar permanentemente la empresa "${empNombre || 'seleccionada'}" y TODOS sus datos asociados (usuarios, clientes, presupuestos, facturas, productos, tareas, etc.)?\n\nEsta acción NO se puede deshacer.`
    );
    if (!confirmado) return;

    clearFeedback();
    try {
      setGuardando(true);
      const { data } = await api.delete(`/empresas/${empId}`);
      setMensaje(data?.mensaje || `Empresa ${empNombre} y todos sus datos eliminados correctamente.`);

      const { data: listado } = await api.get('/empresas');
      const lista = Array.isArray(listado) ? listado : [];
      setEmpresas(lista);

      if (String(empresaId) === String(empId)) {
        const primera = lista[0];
        if (primera?._id) {
          onEmpresaSeleccionada(String(primera._id));
        } else {
          setEmpresaId('');
          setEmpresaActual(null);
        }
      }
    } catch (errEliminar) {
      setError(errEliminar?.response?.data?.error || 'No se pudo eliminar la empresa.');
    } finally {
      setGuardando(false);
    }
  };

  const toggleModulo = (clave) => {
    setEmpresaActual((prev) => ({
      ...prev,
      modulos: {
        ...(prev?.modulos || {}),
        [clave]: !Boolean((prev?.modulos || {})[clave]),
      },
    }));
  };

  const toggleFeature = (clave) => {
    setEmpresaActual((prev) => ({
      ...prev,
      featureFlags: {
        ...(prev?.featureFlags || {}),
        [clave]: !Boolean((prev?.featureFlags || {})[clave]),
      },
    }));
  };

  const probarSlack = async () => {
    if (!empresaActual?._id) return;
    clearFeedback();
    try {
      setProbandoSlack(true);
      const { data } = await api.post(`/empresas/${empresaActual._id}/test-slack`);
      setMensaje(data?.mensaje || 'Prueba de Slack enviada correctamente.');
    } catch (errSlack) {
      setError(errSlack?.response?.data?.error || 'No se pudo enviar la prueba de Slack.');
    } finally {
      setProbandoSlack(false);
    }
  };

  const probarCorreo = async () => {
    if (!esSuperadmin || !empresaActual?._id) return;
    const destino = String(correoPruebaDestino || '').trim();
    if (!destino) {
      setError('Indica un email de destino para la prueba SMTP.');
      return;
    }

    clearFeedback();
    try {
      setProbandoCorreo(true);
      const { data } = await api.post(`/empresas/${empresaActual._id}/test-smtp`, { destino });
      setMensaje(data?.mensaje || 'Correo de prueba enviado correctamente.');
    } catch (errSmtp) {
      setError(errSmtp?.response?.data?.error || 'No se pudo enviar la prueba SMTP.');
    } finally {
      setProbandoCorreo(false);
    }
  };

  const crearTrabajador = async () => {
    if (!empresaActual?._id) return;
    if (!trabajadorNuevo.email.trim() || !trabajadorNuevo.password.trim()) {
      setError('Email y contrasena son obligatorios para crear un trabajador.');
      return;
    }

    clearFeedback();
    try {
      setCreandoTrabajador(true);
      const payload = {
        nombre: trabajadorNuevo.nombre.trim(),
        apellidos: trabajadorNuevo.apellidos.trim(),
        email: trabajadorNuevo.email.trim(),
        telefono: trabajadorNuevo.telefono.trim(),
        puesto: trabajadorNuevo.puesto.trim(),
        password: trabajadorNuevo.password,
        rol: esSuperadmin ? trabajadorNuevo.rol : 'empleado',
        empresaId: empresaActual._id,
      };

      await api.post('/usuarios', payload);
      setTrabajadorNuevo(trabajadorNuevoInicial);
      await cargarTrabajadores(String(empresaActual._id), esSuperadmin);
      setMensaje('Trabajador registrado. Se ha intentado notificar automaticamente por Slack.');
    } catch (errCrearTrabajador) {
      setError(errCrearTrabajador?.response?.data?.error || 'No se pudo crear el trabajador.');
    } finally {
      setCreandoTrabajador(false);
    }
  };

  const onSubirImagen = async (e, destino) => {
    const file = e.target.files?.[0];
    if (!file) return;
    clearFeedback();
    try {
      setSubiendoImagen(true);
      const comprimida = await comprimirImagen(file, destino === 'hero'
        ? { maxWidth: 1920, maxHeight: 1080, quality: 0.82 }
        : { maxWidth: 512, maxHeight: 512, quality: 0.82 });

      if (destino === 'logoEmpresa') actualizarEmpresaActual('logo', comprimida);
      if (destino === 'logoLanding') actualizarLanding('logo', comprimida);
      if (destino === 'hero') actualizarLanding('heroImage', comprimida);
      setMensaje('Imagen cargada y optimizada correctamente.');
    } catch (errImagen) {
      setError(errImagen?.message || 'No se pudo procesar la imagen.');
    } finally {
      setSubiendoImagen(false);
      e.target.value = '';
    }
  };

  const explorarModelosGroq = async () => {
    const apiKey = (apiKeyExploracionGroq || '').trim();
    if (!apiKey) {
      setError('Introduce una API key de Groq para explorar modelos.');
      return;
    }

    clearFeedback();
    setExplorandoModelosGroq(true);
    setResultadosExploracionGroq('');
    try {
      const { data } = await api.post('/presupuestos-ia/explorar-modelos-groq', { apiKey });
      const modelos = Array.isArray(data?.modelos) ? data.modelos : [];
      const texto = [
        `Modelos disponibles (${data?.total || 0}):`,
        ...modelos.map((modelo, index) => `- ${index + 1}. ${modelo.id || modelo.name || '-'} | contexto=${modelo.context_length || '-'} | prompt=${modelo.pricing?.prompt || '-'} | completion=${modelo.pricing?.completion || '-'}`),
      ].join('\n');
      setResultadosExploracionGroq(texto);
      setMensaje('Exploración de modelos de Groq completada.');
    } catch (err) {
      setResultadosExploracionGroq(`Error: ${err?.response?.data?.error || 'No se pudieron explorar los modelos de Groq.'}`);
      setError(err?.response?.data?.error || 'No se pudieron explorar los modelos de Groq.');
    } finally {
      setExplorandoModelosGroq(false);
    }
  };

  const probarIAEmpresa = async (filaIA) => {
    if (!empresaActual?._id) return;
    const prompt = (promptPruebaIA || '').trim() || 'hola';
    clearFeedback();
    setProbandoIA(true);
    setResultadoPruebaIA('');
    setPromptsPruebaIA((prev) => ({ ...prev, [empresaActual._id]: prompt }));
    try {
      const { data } = await api.post('/presupuestos-ia/probar-ia', {
        empresaId: empresaActual._id,
        uso: filaIA?.clave || 'clientes',
        prompt,
        modoPrueba: modoPruebaIA,
        filaIA: {
          nombre: filaIA?.nombre || '',
          reintentos: normalizarReintentosIA(filaIA?.reintentos),
          proveedor: filaIA?.proveedor || 'groq',
          modeloChat: filaIA?.modeloChat || '',
          modeloTranscripcion: filaIA?.modeloTranscripcion || '',
          apiKey: filaIA?.apiKey || '',
        },
      });
      const detalle = data?.detalle || {};
      const intentos = Array.isArray(detalle.attempts) ? detalle.attempts : [];
      const bloque = [
        `Respuesta: ${data?.respuesta || 'Sin respuesta'}`,
        `Proveedor: ${detalle.proveedor || data?.provider || '-'}`,
        `Modelo: ${detalle.modeloChat || data?.modeloChat || '-'}`,
        `Transcripción: ${detalle.modeloTranscripcion || '-'}`,
        `Clave detectada: ${detalle.apiKeyDetectada ? 'Sí' : 'No'}`,
        `Tipo de clave: ${detalle.apiKeyTipo || '-'}`,
        `Prompt: ${detalle.prompt || '-'}`,
        ...(intentos.length ? [`Intentos (${intentos.length}):`, ...intentos.map((attempt, index) => {
          const estado = attempt?.status === 'ok' ? 'OK' : 'ERROR';
          const modelo = attempt?.model || '-';
          const proveedor = attempt?.provider || '-';
          const error = attempt?.error?.message ? ` -> ${attempt.error.message}${attempt.error.code ? ` [${attempt.error.code}]` : ''}` : '';
          return `- ${index + 1}. ${estado} | proveedor=${proveedor} | modelo=${modelo}${error}`;
        })] : []),
      ].filter(Boolean).join('\n');
      setResultadoPruebaIA(bloque);
      setMensaje(`Prueba de IA correcta para ${filaIA?.nombre || 'la fila seleccionada'}.`);
    } catch (err) {
      const detalle = err?.response?.data?.detalle || {};
      const intentos = Array.isArray(detalle.attempts) ? detalle.attempts : [];
      const bloque = [
        `Error: ${err?.response?.data?.error || 'No se pudo probar la IA.'}`,
        `Proveedor: ${detalle.proveedor || '-'}`,
        `Modelo: ${detalle.modeloChat || '-'}`,
        `Transcripción: ${detalle.modeloTranscripcion || '-'}`,
        `Clave detectada: ${detalle.apiKeyDetectada ? 'Sí' : 'No'}`,
        `Prompt: ${detalle.prompt || '-'}`,
        ...(intentos.length ? [`Intentos (${intentos.length}):`, ...intentos.map((attempt, index) => {
          const estado = attempt?.status === 'ok' ? 'OK' : 'ERROR';
          const modelo = attempt?.model || '-';
          const proveedor = attempt?.provider || '-';
          const error = attempt?.error?.message ? ` -> ${attempt.error.message}${attempt.error.code ? ` [${attempt.error.code}]` : ''}` : '';
          return `- ${index + 1}. ${estado} | proveedor=${proveedor} | modelo=${modelo}${error}`;
        })] : []),
      ].filter(Boolean).join('\n');
      setResultadoPruebaIA(bloque);
      setError(err?.response?.data?.error || 'No se pudo probar la IA.');
    } finally {
      setProbandoIA(false);
    }
  };

  const previewSlackAlta = renderPlantilla(
    empresaActual?.slackPlantillaAltaTrabajador ||
      PLANTILLA_SLACK_POR_DEFECTO,
    {
      empresa: empresaActual?.nombre || 'Empresa Demo',
      usuario: [trabajadorNuevo.nombre, trabajadorNuevo.apellidos].filter(Boolean).join(' ') || 'Nuevo Trabajador',
      email: trabajadorNuevo.email || 'trabajador@empresa.com',
      rol: trabajadorNuevo.rol || 'empleado',
      actor: [usuario?.nombre, usuario?.apellidos].filter(Boolean).join(' ') || usuario?.email || 'Administrador',
    }
  );
  const landingSlug = String(empresaActual?.landingPage?.slug || '').trim();
  const landingUrl = landingSlug ? `${window.location.origin}/empresa/${encodeURIComponent(landingSlug)}` : '';

  if (cargando) {
    return (
      <div className="page">
        <div className="trix-loader">
          <div className="trix-spinner" />
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-head page-topbar">
        <div>
          <h1 className="page-title"><FiSettings style={{ marginRight: 8 }} />{vistaGlobal ? 'Ajustes Trix' : 'Empresas'}</h1>
          <p className="page-subtitle">
            Panel simplificado: organizacion por bloques operativos y sin campos tecnicos que no se usan en el dia a dia.
          </p>
        </div>
        <div className="head-actions">
          {esSuperadmin && vistaGlobal ? (
            <button className="btn" onClick={guardarConfiguracionGlobal} disabled={guardando}>
              <FiShield style={{ marginRight: 6 }} /> Guardar ajustes Trix
            </button>
          ) : null}
          {!vistaGlobal ? <button className="btn" onClick={probarSlack} disabled={!empresaActual || probandoSlack}>
            {probandoSlack ? 'Probando Slack...' : 'Probar Slack'}
          </button> : null}
          {!vistaGlobal ? <button className="btn btn-primary" onClick={guardarEmpresa} disabled={!empresaActual || guardando}>
            <FiSave style={{ marginRight: 6 }} /> {guardando ? 'Guardando...' : 'Guardar empresa'}
          </button> : null}
        </div>
      </div>

      {mensaje ? <div className="alert-ok"><FiCheckCircle style={{ marginRight: 8 }} />{mensaje}</div> : null}
      {error ? <div className="alert-error">{error}</div> : null}

      {!vistaGlobal ? <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        <button type="button" className={`btn ${tabActiva === 'empresa' ? 'btn-primary' : ''}`} onClick={() => setTabActiva('empresa')}>Empresa</button>
        <button type="button" className={`btn ${tabActiva === 'landing' ? 'btn-primary' : ''}`} onClick={() => setTabActiva('landing')}>Landing</button>
        <button type="button" className={`btn ${tabActiva === 'equipo' ? 'btn-primary' : ''}`} onClick={() => setTabActiva('equipo')}>Equipo</button>
        <button type="button" className={`btn ${tabActiva === 'comunicaciones' ? 'btn-primary' : ''}`} onClick={() => setTabActiva('comunicaciones')}>Comunicaciones</button>
        {esSuperadmin || esAdminEmpresa ? <button type="button" className={`btn ${tabActiva === 'ia' ? 'btn-primary' : ''}`} onClick={() => setTabActiva('ia')}>{esSuperadmin ? 'IAs de empresa' : 'Cuotas de IA'}</button> : null}
        {esSuperadmin ? (
          <>
            <button type="button" className={`btn ${tabActiva === 'permisos' ? 'btn-primary' : ''}`} onClick={() => setTabActiva('permisos')}>Modulos y flags</button>
            <button type="button" className={`btn ${tabActiva === 'ventas' ? 'btn-primary' : ''}`} onClick={() => setTabActiva('ventas')}><FiTrendingUp style={{ marginRight: 6 }} />Gestión de ventas</button>
          </>
        ) : null}
      </div> : null}

      {esSuperadmin && vistaGlobal ? (
        <>
          <Seccion title="Ajustes Trix" subtitle="Solo campos globales operativos" icon={<FiShield />}>
            <div className="form-grid">
              <label className="field">
                <span>Nombre de la app</span>
                <input value={globalConfig.appName || ''} onChange={(e) => setGlobalConfig((p) => ({ ...p, appName: e.target.value }))} />
              </label>
              <label className="field">
                <span>Email soporte</span>
                <input value={globalConfig.soporteEmail || ''} onChange={(e) => setGlobalConfig((p) => ({ ...p, soporteEmail: e.target.value }))} />
              </label>
              <label className="field">
                <span>Telefono soporte</span>
                <input value={globalConfig.soporteTelefono || ''} onChange={(e) => setGlobalConfig((p) => ({ ...p, soporteTelefono: e.target.value }))} />
              </label>
              <label className="field">
                <span>Zona horaria</span>
                <input value={globalConfig.timezone || 'Europe/Madrid'} onChange={(e) => setGlobalConfig((p) => ({ ...p, timezone: e.target.value }))} />
              </label>
              <label className="field">
                <span>Idioma</span>
                <select value={globalConfig.idioma || 'es'} onChange={(e) => setGlobalConfig((p) => ({ ...p, idioma: e.target.value }))}>
                  <option value="es">Espanol</option>
                  <option value="en">Ingles</option>
                </select>
              </label>
              <label className="field checkbox" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Alertas Slack activas</span>
                <input type="checkbox" checked={Boolean(globalConfig.alertasSlack)} onChange={(e) => setGlobalConfig((p) => ({ ...p, alertasSlack: e.target.checked }))} />
              </label>
              <label className="field checkbox" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Demo publica activa</span>
                <input type="checkbox" checked={Boolean(globalConfig.demoPublicaActiva)} onChange={(e) => setGlobalConfig((p) => ({ ...p, demoPublicaActiva: e.target.checked }))} />
              </label>
            </div>
          </Seccion>

          <Seccion title="Plantillas globales de landing" subtitle="Crea y gestiona estilos visuales reutilizables para empresas" icon={<FiImage />}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, gap: 10, flexWrap: 'wrap' }}>
              <div className="muted">Estas plantillas son la base visual para todas las empresas.</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button type="button" className="btn btn-primary" onClick={crearPlantillaGlobal}>Añadir plantilla</button>
                <button type="button" className="btn" onClick={() => guardarConfiguracionGlobal()} disabled={guardando}>Guardar catálogo</button>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 2fr', gap: 18 }}>
              <div style={{ display: 'grid', gap: 12 }}>
                {(Array.isArray(globalConfig.landingTemplates) ? globalConfig.landingTemplates : []).map((plantilla) => (
                  <button
                    key={plantilla?.id || plantilla?.nombre}
                    type="button"
                    onClick={() => setPlantillaGlobalActiva(plantilla?.id || '')}
                    style={{
                      textAlign: 'left',
                      border: plantilla?.id === plantillaGlobalActiva ? '2px solid #4f46e5' : '1px solid var(--border)',
                      borderRadius: 12,
                      padding: 12,
                      background: '#fff',
                      cursor: 'pointer',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                      <strong>{plantilla?.nombre || 'Plantilla'}</strong>
                      <span className="muted" style={{ fontSize: 11 }}>{plantilla?.categoria || 'custom'}</span>
                    </div>
                    <div style={{ color: '#64748b', fontSize: 12, marginTop: 8 }}>{plantilla?.descripcion || 'Plantilla personalizada'}</div>
                  </button>
                ))}
              </div>

              <div style={{ border: '1px solid var(--border)', borderRadius: 16, padding: 14, background: '#f8fafc' }}>
                {(() => {
                  const plantillaActiva = (Array.isArray(globalConfig.landingTemplates) ? globalConfig.landingTemplates : []).find((plantilla) => plantilla?.id === plantillaGlobalActiva) || null;
                  if (!plantillaActiva) return null;

                  return (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
                        <strong>{plantillaActiva.nombre || 'Plantilla'}</strong>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          <button type="button" className="btn" onClick={() => aplicarPlantillaGlobalAEmpresa(plantillaActiva)} disabled={!empresaActual}>Aplicar a empresa</button>
                          <button type="button" className="btn" onClick={() => duplicarPlantillaGlobal(plantillaActiva)}>Duplicar</button>
                          <button type="button" className="btn btn-danger" onClick={() => eliminarPlantillaGlobal(plantillaActiva.id)}>Eliminar</button>
                        </div>
                      </div>

                      <div style={{ marginBottom: 16 }}>
                        <VistaPreviaPlantillaGlobal plantilla={plantillaActiva} nombreEmpresa={empresaActual?.nombre || 'Empresa'} />
                      </div>

                      <div className="form-grid">
                        <label className="field">
                          <span>Nombre</span>
                          <input value={plantillaActiva?.nombre || ''} onChange={(e) => actualizarPlantillaGlobal(plantillaActiva?.id, 'nombre', e.target.value)} />
                        </label>
                        <label className="field">
                          <span>Categoría</span>
                          <input value={plantillaActiva?.categoria || ''} onChange={(e) => actualizarPlantillaGlobal(plantillaActiva?.id, 'categoria', e.target.value)} />
                        </label>
                        <label className="field">
                          <span>Layout</span>
                          <input value={plantillaActiva?.layout || ''} onChange={(e) => actualizarPlantillaGlobal(plantillaActiva?.id, 'layout', e.target.value)} />
                        </label>
                        <label className="field">
                          <span>Color primario</span>
                          <input type="color" value={plantillaActiva?.colorPrimario || '#4f46e5'} onChange={(e) => actualizarPlantillaGlobal(plantillaActiva?.id, 'colorPrimario', e.target.value)} />
                        </label>
                        <label className="field">
                          <span>Color secundario</span>
                          <input type="color" value={plantillaActiva?.colorSecundario || '#0ea5e9'} onChange={(e) => actualizarPlantillaGlobal(plantillaActiva?.id, 'colorSecundario', e.target.value)} />
                        </label>
                        <label className="field">
                          <span>Badge</span>
                          <input value={plantillaActiva?.heroBadge || ''} onChange={(e) => actualizarPlantillaGlobal(plantillaActiva?.id, 'heroBadge', e.target.value)} />
                        </label>
                        <label className="field">
                          <span>Título</span>
                          <input value={plantillaActiva?.titulo || ''} onChange={(e) => actualizarPlantillaGlobal(plantillaActiva?.id, 'titulo', e.target.value)} />
                        </label>
                        <label className="field">
                          <span>Slogan</span>
                          <input value={plantillaActiva?.slogan || ''} onChange={(e) => actualizarPlantillaGlobal(plantillaActiva?.id, 'slogan', e.target.value)} />
                        </label>
                        <label className="field">
                          <span>Texto CTA</span>
                          <input value={plantillaActiva?.ctaTexto || ''} onChange={(e) => actualizarPlantillaGlobal(plantillaActiva?.id, 'ctaTexto', e.target.value)} />
                        </label>
                        <label className="field">
                          <span>Link CTA</span>
                          <input value={plantillaActiva?.ctaLink || ''} onChange={(e) => actualizarPlantillaGlobal(plantillaActiva?.id, 'ctaLink', e.target.value)} />
                        </label>
                        <label className="field" style={{ gridColumn: '1 / -1' }}>
                          <span>Descripción</span>
                          <textarea value={plantillaActiva?.descripcionLanding || plantillaActiva?.descripcion || ''} rows={3} onChange={(e) => actualizarPlantillaGlobal(plantillaActiva?.id, 'descripcionLanding', e.target.value)} />
                        </label>
                      </div>

                      <div style={{ marginTop: 18, border: '1px solid var(--border)', borderRadius: 12, padding: 12, background: '#fff' }}>
                        <strong style={{ display: 'block', marginBottom: 10 }}>Secciones del layout</strong>
                        <div style={{ display: 'grid', gap: 8 }}>
                          {(Array.isArray(plantillaActiva?.componentes) ? plantillaActiva.componentes : []).map((componente, index) => (
                            <div
                              key={`${plantillaActiva.id}-${componente}-${index}`}
                              draggable
                              onDragStart={(event) => {
                                event.dataTransfer.setData('text/plain', JSON.stringify({ id: plantillaActiva.id, index }));
                                event.dataTransfer.effectAllowed = 'move';
                              }}
                              onDragOver={(event) => event.preventDefault()}
                              onDrop={(event) => {
                                event.preventDefault();
                                try {
                                  const payload = JSON.parse(event.dataTransfer.getData('text/plain') || '{}');
                                  if (payload?.id === plantillaActiva.id && Number.isInteger(payload.index) && payload.index !== index) {
                                    moverComponentePlantillaGlobal(plantillaActiva.id, payload.index, index);
                                  }
                                } catch (err) {
                                  console.error('ERROR drag drop plantilla ->', err?.stack || err);
                                }
                              }}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 8,
                                border: '1px solid var(--border)',
                                borderRadius: 10,
                                padding: '8px 10px',
                                background: '#f8fafc',
                                cursor: 'move',
                              }}
                            >
                              <span style={{ textTransform: 'capitalize', fontWeight: 600 }}>{componente}</span>
                              <div style={{ display: 'flex', gap: 6 }}>
                                <button type="button" className="btn" disabled={index === 0} onClick={() => moverComponentePlantillaGlobal(plantillaActiva.id, index, index - 1)}>↑</button>
                                <button type="button" className="btn" disabled={index === (Array.isArray(plantillaActiva?.componentes) ? plantillaActiva.componentes.length - 1 : 0)} onClick={() => moverComponentePlantillaGlobal(plantillaActiva.id, index, index + 1)}>↓</button>
                                <button type="button" className="btn btn-danger" onClick={() => toggleComponentePlantillaGlobal(plantillaActiva.id, componente)}>Quitar</button>
                              </div>
                            </div>
                          ))}
                        </div>

                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
                          {['hero', 'servicios', 'beneficios', 'stats', 'testimonials'].filter((componente) => !(Array.isArray(plantillaActiva?.componentes) ? plantillaActiva.componentes.includes(componente) : false)).map((componente) => (
                            <button key={componente} type="button" className="btn" onClick={() => toggleComponentePlantillaGlobal(plantillaActiva.id, componente)}>
                              + {componente}
                            </button>
                          ))}
                        </div>
                      </div>

                      {['servicios', 'beneficios', 'stats', 'testimonials'].map((campo) => {
                        const lista = Array.isArray(plantillaActiva?.[campo]) ? plantillaActiva[campo] : [];
                        if (!lista.length && campo !== 'servicios') return null;

                        return (
                          <div key={campo} style={{ marginTop: 18, border: '1px solid var(--border)', borderRadius: 12, padding: 12, background: '#fff' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, gap: 8 }}>
                              <strong>{campo}</strong>
                              <button type="button" className="btn" onClick={() => anadirItemPlantillaGlobal(plantillaActiva.id, campo, 'Nuevo elemento')}>Añadir</button>
                            </div>
                            <div style={{ display: 'grid', gap: 8 }}>
                              {lista.map((item, index) => (
                                <div key={`${campo}-${index}`} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                  <input
                                    value={item || ''}
                                    onChange={(e) => actualizarListaPlantillaGlobal(plantillaActiva.id, campo, index, e.target.value)}
                                    style={{ flex: 1 }}
                                  />
                                  <button type="button" className="btn btn-danger" onClick={() => eliminarItemPlantillaGlobal(plantillaActiva.id, campo, index)}>Quitar</button>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </>
                  );
                })()}
              </div>
            </div>
          </Seccion>
        </>
      ) : null}

      {esSuperadmin && !vistaGlobal ? (
        <Seccion title="Empresa activa" subtitle="Selecciona empresa para editar" icon={<FiBriefcase />}>
          <div className="field" style={{ marginBottom: 0 }}>
            <span>Empresa</span>
            <select value={empresaId} onChange={(e) => onEmpresaSeleccionada(e.target.value)}>
              {!empresaId ? <option value="">Selecciona empresa</option> : null}
              {empresas.map((item) => (
                <option key={item._id} value={item._id}>{item.nombre}</option>
              ))}
            </select>
          </div>
          {empresaActual?.logo ? (
            <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
              <img src={empresaActual.logo} alt={empresaActual.nombre} style={{ width: 40, height: 40, borderRadius: 10, objectFit: 'cover', border: '1px solid var(--border)' }} />
              <span className="muted">Logo actual de la empresa seleccionada</span>
            </div>
          ) : null}
        </Seccion>
      ) : null}

      {esSuperadmin && tabActiva === 'ventas' ? (
        <Seccion title="Gestión de ventas" subtitle="Leads captados desde la landing y conversión a empresa" icon={<FiTrendingUp />}>
          <div className="alert-info" style={{ marginBottom: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
            <div>
              <strong>Clientes Potenciales:</strong> Ahora se encuentra accesible directamente desde el menú principal lateral debajo de <strong>Clientes</strong>.
            </div>
            <Link to="/app/clientes-potenciales" className="btn btn-primary">
              Abrir Clientes Potenciales ↗
            </Link>
          </div>
          {leadConversion ? (
            <div style={{ border: '1px solid var(--border)', borderRadius: 14, padding: 14, marginBottom: 14, background: '#f8fafc' }}>
              <strong>Convertir lead a empresa</strong>
              <div className="form-grid" style={{ marginTop: 10 }}>
                <label className="field">
                  <span>Nombre de empresa</span>
                  <input value={leadConversion.nombreEmpresa || ''} onChange={(e) => actualizarConversionLead('nombreEmpresa', e.target.value)} />
                </label>
                <label className="field">
                  <span>Nombre del administrador</span>
                  <input value={leadConversion.nombreAdmin || ''} onChange={(e) => actualizarConversionLead('nombreAdmin', e.target.value)} />
                </label>
                <label className="field">
                  <span>Email de acceso</span>
                  <input type="email" value={leadConversion.email || ''} onChange={(e) => actualizarConversionLead('email', e.target.value)} />
                </label>
                <label className="field">
                  <span>Contraseña inicial</span>
                  <PasswordInput value={leadConversion.password || ''} onChange={(e) => actualizarConversionLead('password', e.target.value)} />
                </label>
                <label className="field">
                  <span>Tipo de negocio</span>
                  <select value={leadConversion.tipoNegocio || 'general'} onChange={(e) => actualizarConversionLead('tipoNegocio', e.target.value)}>
                    {TIPOS_NEGOCIO.map((tipo) => <option key={tipo.value} value={tipo.value}>{tipo.label}</option>)}
                  </select>
                </label>
                <label className="field">
                  <span>Plan</span>
                  <select value={leadConversion.plan || 'starter'} onChange={(e) => actualizarConversionLead('plan', e.target.value)}>
                    {PLANES.map((plan) => <option key={plan.value} value={plan.value}>{plan.label}</option>)}
                  </select>
                </label>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                <button className="btn btn-primary" onClick={convertirLead} disabled={convirtiendoLead}>{convirtiendoLead ? 'Convirtiendo...' : 'Crear empresa y administrador'}</button>
                <button className="btn" onClick={() => setLeadConversion(null)}>Cancelar</button>
              </div>
            </div>
          ) : null}

          {cargandoClientesPotenciales ? <div className="muted">Cargando leads...</div> : null}
          {!cargandoClientesPotenciales && clientesPotenciales.length === 0 ? <div className="muted">No hay leads de ventas registrados todavía.</div> : null}
          {clientesPotenciales.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>Email</th>
                    <th>Empresa</th>
                    <th>Interés</th>
                    <th>Estado</th>
                    <th>Origen</th>
                    <th>Fecha</th>
                    <th>Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {clientesPotenciales.map((lead) => (
                    <tr key={lead._id}>
                      <td>{lead.nombre || '-'}</td>
                      <td>{lead.email || '-'}</td>
                      <td>{lead.empresa || '-'}</td>
                      <td>{lead.interes || '-'}</td>
                      <td>{lead.estado || '-'}</td>
                      <td>{lead.origen || '-'}</td>
                      <td>{lead.createdAt ? new Date(lead.createdAt).toLocaleDateString('es-ES') : '-'}</td>
                      <td>
                        {lead.estado === 'vendido' ? (
                          <span className="muted">Convertido</span>
                        ) : (
                          <button className="btn" onClick={() => iniciarConversionLead(lead)}>Convertir</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </Seccion>
      ) : null}

      {!vistaGlobal && empresaActual ? (
        <>
          {tabActiva === 'empresa' ? (
          <>
            {esSuperadmin && !vistaGlobal ? (
              <Seccion title="Empresas registradas" subtitle="Gestión global de empresas y eliminación" icon={<FiBriefcase />}>
                {empresas.length === 0 ? (
                  <div className="muted">No hay empresas registradas.</div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Nombre</th>
                          <th>CIF</th>
                          <th>Email</th>
                          <th>Tipo de negocio</th>
                          <th>Plan</th>
                          <th>Estado</th>
                          <th>Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {empresas.map((item) => (
                          <tr key={item._id} style={{ background: String(item._id) === String(empresaId) ? 'rgba(79, 70, 229, 0.05)' : undefined }}>
                            <td><strong>{item.nombre}</strong></td>
                            <td>{item.cif || '—'}</td>
                            <td>{item.email || '—'}</td>
                            <td>{item.tipoNegocio || 'general'}</td>
                            <td><span className="badge badge-info">{item.plan || 'starter'}</span></td>
                            <td>{item.activa === false ? <span className="badge badge-danger">Inactiva</span> : <span className="badge badge-ok">Activa</span>}</td>
                            <td>
                              <div style={{ display: 'flex', gap: 6 }}>
                                <button
                                  type="button"
                                  className={`btn btn-sm ${String(item._id) === String(empresaId) ? 'btn-primary' : ''}`}
                                  onClick={() => onEmpresaSeleccionada(item._id)}
                                >
                                  {String(item._id) === String(empresaId) ? 'Seleccionada' : 'Editar'}
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm danger"
                                  onClick={() => eliminarEmpresa(item._id, item.nombre)}
                                  title="Eliminar empresa y todos sus datos"
                                >
                                  <FiTrash2 /> Borrar
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Seccion>
            ) : null}

            {esSuperadmin && !vistaGlobal ? (
              <Seccion title="Alta de empresa" subtitle="Crear una nueva empresa y asignar su plan inicial" icon={<FiPlusCircle />}>
                <div className="form-grid" style={{ marginBottom: 14 }}>
                  <label className="field">
                    <span>Nombre</span>
                    <input value={nuevaEmpresa.nombre} onChange={(e) => setNuevaEmpresa((p) => ({ ...p, nombre: e.target.value }))} placeholder="Nueva empresa" />
                  </label>
                  <label className="field">
                    <span>CIF</span>
                    <input value={nuevaEmpresa.cif} onChange={(e) => setNuevaEmpresa((p) => ({ ...p, cif: e.target.value }))} placeholder="B12345678" />
                  </label>
                  <label className="field">
                    <span>Email *</span>
                    <input required type="email" value={nuevaEmpresa.email} onChange={(e) => setNuevaEmpresa((p) => ({ ...p, email: e.target.value }))} placeholder="info@empresa.com" />
                  </label>
                  <label className="field">
                    <span>Telefono</span>
                    <input value={nuevaEmpresa.telefono} onChange={(e) => setNuevaEmpresa((p) => ({ ...p, telefono: e.target.value }))} placeholder="+34 600 000 000" />
                  </label>
                  <label className="field span-2">
                    <span>Direccion</span>
                    <input value={nuevaEmpresa.direccion} onChange={(e) => setNuevaEmpresa((p) => ({ ...p, direccion: e.target.value }))} />
                  </label>
                  <label className="field">
                    <span>Tipo de negocio</span>
                    <select value={nuevaEmpresa.tipoNegocio} onChange={(e) => setNuevaEmpresa((p) => ({ ...p, tipoNegocio: e.target.value }))}>
                      {TIPOS_NEGOCIO.map((tipo) => <option key={tipo.value} value={tipo.value}>{tipo.label}</option>)}
                    </select>
                  </label>
                  <label className="field">
                    <span>Plan</span>
                    <select value={nuevaEmpresa.plan} onChange={(e) => setNuevaEmpresa((p) => ({ ...p, plan: e.target.value }))}>
                      {PLANES.map((plan) => <option key={plan.value} value={plan.value}>{plan.label}</option>)}
                    </select>
                  </label>
                </div>
                <button className="btn btn-primary" onClick={crearEmpresa} disabled={creandoEmpresa}>
                  {creandoEmpresa ? 'Creando...' : 'Crear empresa'}
                </button>
              </Seccion>
            ) : null}
            <Seccion title="Datos de empresa" subtitle="Identidad, plan y contacto" icon={<FiSettings />}>
              <div className="form-grid">
                <label className="field span-2">
                <span>Nombre</span>
                <input value={empresaActual.nombre || ''} onChange={(e) => actualizarEmpresaActual('nombre', e.target.value)} />
              </label>
              <label className="field">
                <span>CIF</span>
                <input value={empresaActual.cif || ''} onChange={(e) => actualizarEmpresaActual('cif', e.target.value)} />
              </label>
              <label className="field">
                <span>Email *</span>
                <input required type="email" value={empresaActual.email || ''} onChange={(e) => actualizarEmpresaActual('email', e.target.value)} />
              </label>
              <label className="field span-2">
                <span>Direccion</span>
                <input value={empresaActual.direccion || ''} onChange={(e) => actualizarEmpresaActual('direccion', e.target.value)} />
              </label>
              <label className="field">
                <span>Telefono</span>
                <input value={empresaActual.telefono || ''} onChange={(e) => actualizarEmpresaActual('telefono', e.target.value)} />
              </label>
              <label className="field span-2">
                <span>Logo empresa (URL o base64)</span>
                <input value={empresaActual.logo || ''} onChange={(e) => actualizarEmpresaActual('logo', e.target.value)} placeholder="https://... o data:image/..." />
              </label>
              <label className="field span-2">
                <span>Subir logo empresa (se comprime automaticamente)</span>
                <input type="file" accept="image/*" onChange={(e) => onSubirImagen(e, 'logoEmpresa')} disabled={subiendoImagen} />
              </label>
              {esSuperadmin ? (
                <>
                  <label className="field span-2">
                    <span>Observación por defecto para presupuestos</span>
                    <textarea
                      rows={3}
                      value={empresaActual.observacionDefectoPresupuesto ?? empresaActual.observacionDefecto ?? ''}
                      onChange={(e) => actualizarEmpresaActual('observacionDefectoPresupuesto', e.target.value)}
                      placeholder="Ej: Validez del presupuesto: 15 días..."
                    />
                    <small className="muted">Se cargará automáticamente en los nuevos presupuestos.</small>
                  </label>
                  <label className="field span-2">
                    <span>Observación por defecto para facturas</span>
                    <textarea
                      rows={3}
                      value={empresaActual.observacionDefectoFactura ?? empresaActual.observacionDefecto ?? ''}
                      onChange={(e) => actualizarEmpresaActual('observacionDefectoFactura', e.target.value)}
                      placeholder="Ej: Pago mediante transferencia al IBAN..."
                    />
                    <small className="muted">Se cargará automáticamente en las nuevas facturas.</small>
                  </label>
                  <label className="field span-2">
                    <span>Texto del pie del presupuesto</span>
                    <textarea
                      rows={3}
                      value={empresaActual.textoFooterPresupuesto || ''}
                      onChange={(e) => actualizarEmpresaActual('textoFooterPresupuesto', e.target.value)}
                      placeholder="Gracias por confiar en nosotros"
                    />
                    <small className="muted">Se mostrará justo después de “Presupuesto emitido por {empresaActual?.nombre || 'Empresa'}” en el PDF.</small>
                  </label>
                  <label className="field span-2">
                    <span>Texto del pie de la factura</span>
                    <textarea
                      rows={3}
                      value={empresaActual.textoFooterFactura || ''}
                      onChange={(e) => actualizarEmpresaActual('textoFooterFactura', e.target.value)}
                      placeholder="Gracias por confiar en nosotros"
                    />
                    <small className="muted">Se mostrará justo después de “Factura emitida por {empresaActual?.nombre || 'Empresa'}” en el PDF.</small>
                  </label>
                  <label className="field span-2">
                    <span>API key Groq por empresa</span>
                    <PasswordInput
                      value={empresaActual.ia?.groqApiKey || ''}
                      onChange={(e) => actualizarEmpresaActual('ia', { ...(empresaActual.ia || {}), groqApiKey: e.target.value })}
                      placeholder={empresaActual.groqApiKeyConfigurada ? 'Configurada. Escribe una nueva para reemplazarla.' : 'gsk_...'}
                      autoComplete="new-password"
                    />
                    <small className="muted">{empresaActual.groqApiKeyConfigurada ? 'Hay una clave guardada; nunca se muestra.' : 'Si queda vacio, se usara la clave global del entorno como fallback.'}</small>
                  </label>
                  <label className="field">
                    <span>Tipo de negocio</span>
                    <select value={empresaActual.tipoNegocio || 'general'} onChange={(e) => actualizarEmpresaActual('tipoNegocio', e.target.value)}>
                      {TIPOS_NEGOCIO.map((tipo) => <option key={tipo.value} value={tipo.value}>{tipo.label}</option>)}
                    </select>
                  </label>
                  <label className="field">
                    <span>Plan</span>
                    <select value={empresaActual.plan || 'starter'} onChange={(e) => actualizarEmpresaActual('plan', e.target.value)}>
                      {PLANES.map((plan) => <option key={plan.value} value={plan.value}>{plan.label}</option>)}
                    </select>
                  </label>
                  <label className="field">
                    <span>Estado</span>
                    <select value={empresaActual.activa === false ? 'inactiva' : 'activa'} onChange={(e) => actualizarEmpresaActual('activa', e.target.value === 'activa')}>
                      <option value="activa">Activa</option>
                      <option value="inactiva">Inactiva</option>
                    </select>
                  </label>
                  <label className="field">
                    <span>Creditos ITV</span>
                    <input type="number" min="0" value={empresaActual.itvCreditos || 0} onChange={(e) => actualizarEmpresaActual('itvCreditos', Number(e.target.value) || 0)} />
                  </label>
                </>
              ) : null}
            </div>

              {empresaActual.logo ? (
                <div style={{ marginTop: 12 }}>
                  <img src={empresaActual.logo} alt={empresaActual.nombre} style={{ width: 70, height: 70, borderRadius: 12, objectFit: 'cover', border: '1px solid var(--border)' }} />
                </div>
              ) : null}

              {esSuperadmin && empresaActual?._id ? (
                <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px dashed var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
                  <div>
                    <strong style={{ color: '#dc2626' }}>Zona de peligro: Borrar esta empresa</strong>
                    <p style={{ margin: 0, fontSize: 12, color: 'var(--text-soft)' }}>
                      Elimina de forma permanente la empresa <strong>{empresaActual.nombre}</strong> y todos sus datos (usuarios, clientes, facturas, presupuestos, etc.).
                    </p>
                  </div>
                  <button
                    type="button"
                    className="btn danger"
                    onClick={() => eliminarEmpresa(empresaActual._id, empresaActual.nombre)}
                  >
                    <FiTrash2 style={{ marginRight: 6 }} /> Borrar empresa y sus datos
                  </button>
                </div>
              ) : null}
            </Seccion>
          </>
          ) : null}

          {tabActiva === 'landing' ? (
          <Seccion title="Landing publica" subtitle="Logo de landing, cabecera y mensaje comercial" icon={<FiImage />}>
            <div className="alert-info" style={{ marginBottom: 16 }}>
              <strong>Editor visual activo:</strong> esta previsualización refleja cómo quedará la landing pública mientras editas. Si quieres ver la versión publicada, abre la landing pública desde el enlace de abajo o desde la ruta <strong>/empresa/:slug</strong>.
            </div>
            <div style={{ marginBottom: 16 }}>
              <VistaPreviaLandingVisual empresaActual={empresaActual} />
            </div>
            <div style={{ display: 'grid', gap: 20, gridTemplateColumns: 'minmax(0, 1.15fr) minmax(320px, 0.85fr)', alignItems: 'start' }}>
              <div className="form-grid">
                <label className="field">
                  <span>Slug publico</span>
                  <input value={empresaActual.landingPage?.slug || ''} onChange={(e) => actualizarLanding('slug', e.target.value)} placeholder="mi-negocio" />
                  {landingUrl ? (
                    <a className="link" href={landingUrl} target="_blank" rel="noopener noreferrer">
                      Abrir landing publica <span aria-hidden="true">↗</span>
                    </a>
                  ) : <small className="muted">Informa el slug para generar el enlace publico.</small>}
                </label>
                <label className="field checkbox" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>Landing activa</span>
                  <input type="checkbox" checked={Boolean(empresaActual.landingPage?.activo)} onChange={(e) => actualizarLanding('activo', e.target.checked)} />
                </label>
                <label className="field span-2">
                  <span>Logo de landing (opcional, si se deja vacio usa el logo de empresa)</span>
                  <input value={empresaActual.landingPage?.logo || ''} onChange={(e) => actualizarLanding('logo', e.target.value)} placeholder="https://... o data:image/..." />
                </label>
                <label className="field span-2">
                  <span>Subir logo de landing (se comprime automaticamente)</span>
                  <input type="file" accept="image/*" onChange={(e) => onSubirImagen(e, 'logoLanding')} disabled={subiendoImagen} />
                </label>
                <label className="field span-2">
                  <span>Imagen de cabecera (hero)</span>
                  <input value={empresaActual.landingPage?.heroImage || ''} onChange={(e) => actualizarLanding('heroImage', e.target.value)} placeholder="https://..." />
                </label>
                <label className="field span-2">
                  <span>Subir imagen cabecera (se optimiza para web)</span>
                  <input type="file" accept="image/*" onChange={(e) => onSubirImagen(e, 'hero')} disabled={subiendoImagen} />
                </label>
                <label className="field span-2">
                  <span>Titulo principal</span>
                  <input value={empresaActual.landingPage?.titulo || ''} onChange={(e) => actualizarLanding('titulo', e.target.value)} placeholder="Gestion digital para tu negocio" />
                </label>
                <label className="field">
                  <span>Slogan</span>
                  <input value={empresaActual.landingPage?.slogan || ''} onChange={(e) => actualizarLanding('slogan', e.target.value)} placeholder="Mas control, menos caos" />
                </label>
                <label className="field">
                  <span>Texto CTA</span>
                  <input value={empresaActual.landingPage?.ctaTexto || ''} onChange={(e) => actualizarLanding('ctaTexto', e.target.value)} placeholder="Solicitar demo" />
                </label>
                <label className="field">
                  <span>Badge hero</span>
                  <input value={empresaActual.landingPage?.heroBadge || ''} onChange={(e) => actualizarLanding('heroBadge', e.target.value)} placeholder="Gestión conectada para PYMEs" />
                </label>
                <label className="field">
                  <span>Enlace CTA</span>
                  <input value={empresaActual.landingPage?.ctaLink || ''} onChange={(e) => actualizarLanding('ctaLink', e.target.value)} placeholder="/login o https://..." />
                </label>
                <label className="field">
                  <span>Variación visual</span>
                  <select value={empresaActual.landingPage?.layoutVariant || 'clasico'} onChange={(e) => actualizarLanding('layoutVariant', e.target.value)}>
                    <option value="clasico">Clásico</option>
                    <option value="moderno">Moderno</option>
                    <option value="minimalista">Minimalista</option>
                  </select>
                </label>
                <label className="field">
                  <span>Email comercial</span>
                  <input value={empresaActual.landingPage?.email || ''} onChange={(e) => actualizarLanding('email', e.target.value)} placeholder="info@empresa.com" />
                </label>
                <label className="field">
                  <span>Telefono comercial</span>
                  <input value={empresaActual.landingPage?.telefono || ''} onChange={(e) => actualizarLanding('telefono', e.target.value)} />
                </label>
                <label className="field">
                  <span>WhatsApp</span>
                  <input value={empresaActual.landingPage?.whatsapp || ''} onChange={(e) => actualizarLanding('whatsapp', e.target.value)} placeholder="34600000000" />
                </label>
                <label className="field span-2">
                  <span>Descripcion</span>
                  <textarea rows={3} value={empresaActual.landingPage?.descripcion || ''} onChange={(e) => actualizarLanding('descripcion', e.target.value)} />
                </label>
                <label className="field">
                  <span>Color primario</span>
                  <input type="color" value={empresaActual.landingPage?.colorPrimario || '#4f46e5'} onChange={(e) => actualizarLanding('colorPrimario', e.target.value)} />
                </label>
                <label className="field">
                  <span>Color secundario</span>
                  <input type="color" value={empresaActual.landingPage?.colorSecundario || '#0ea5e9'} onChange={(e) => actualizarLanding('colorSecundario', e.target.value)} />
                </label>
                <label className="field span-2">
                  <span>Servicios (uno por linea)</span>
                  <textarea rows={4} value={normalizarListaLanding(empresaActual.landingPage?.servicios).join('\n')} onChange={(e) => actualizarLanding('servicios', toArrayDesdeTexto(e.target.value))} />
                </label>
                <label className="field span-2">
                  <span>Beneficios (uno por linea)</span>
                  <textarea rows={4} value={normalizarListaLanding(empresaActual.landingPage?.beneficios).join('\n')} onChange={(e) => actualizarLanding('beneficios', toArrayDesdeTexto(e.target.value))} />
                </label>
                <label className="field span-2">
                  <span>Indicadores (uno por línea)</span>
                  <textarea rows={3} value={normalizarListaLanding(empresaActual.landingPage?.stats).join('\n')} onChange={(e) => actualizarLanding('stats', toArrayDesdeTexto(e.target.value))} placeholder="Ej. +120 clientes gestionados" />
                </label>
                <label className="field span-2">
                  <span>Testimonios (uno por línea)</span>
                  <textarea rows={3} value={normalizarListaLanding(empresaActual.landingPage?.testimonials).join('\n')} onChange={(e) => actualizarLanding('testimonials', toArrayDesdeTexto(e.target.value))} placeholder="Ej. “Trix nos ahorró 8 horas a la semana”" />
                </label>
                <label className="field span-2">
                  <span>HTML libre adicional</span>
                  <textarea rows={5} value={empresaActual.landingPage?.htmlLibre || ''} onChange={(e) => actualizarLanding('htmlLibre', e.target.value)} placeholder="Puedes incluir bloques HTML, botones o listas personalizados" />
                </label>
                <label className="field span-2">
                  <span>Bloques configurables</span>
                  <div style={{ display: 'grid', gap: 12 }}>
                    <button type="button" onClick={() => {
                      const bloques = Array.isArray(empresaActual.landingPage?.bloques) ? empresaActual.landingPage.bloques : [];
                      actualizarLanding('bloques', [...bloques, crearBloqueLanding(bloques.length)]);
                    }} style={{ justifySelf: 'flex-start', border: '1px solid var(--border)', background: '#fff', borderRadius: 999, padding: '8px 12px', cursor: 'pointer' }}>
                      + Añadir bloque
                    </button>
                    {(empresaActual.landingPage?.bloques || []).length === 0 ? (
                      <div className="muted">Todavía no hay bloques. Añade uno para mostrar contenido extra en la landing.</div>
                    ) : null}
                    {(empresaActual.landingPage?.bloques || []).map((bloque, index) => (
                      <div key={bloque.id || `${bloque.tipo}-${index}`} style={{ border: '1px solid var(--border)', borderRadius: 14, padding: 12, background: '#f8fafc' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
                          <strong>{index + 1}. {bloque.titulo || 'Nuevo bloque'}</strong>
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            <button type="button" onClick={() => {
                              const bloques = [...(empresaActual.landingPage?.bloques || [])];
                              const reordenados = reordenarBloquesLanding(bloques, index, index - 1);
                              actualizarLanding('bloques', reordenados);
                            }} style={{ border: '1px solid var(--border)', background: '#fff', borderRadius: 8, padding: '6px 8px', cursor: 'pointer' }}>↑</button>
                            <button type="button" onClick={() => {
                              const bloques = [...(empresaActual.landingPage?.bloques || [])];
                              const reordenados = reordenarBloquesLanding(bloques, index, index + 1);
                              actualizarLanding('bloques', reordenados);
                            }} style={{ border: '1px solid var(--border)', background: '#fff', borderRadius: 8, padding: '6px 8px', cursor: 'pointer' }}>↓</button>
                            <button type="button" onClick={() => {
                              const bloques = (empresaActual.landingPage?.bloques || []).filter((_, i) => i !== index);
                              actualizarLanding('bloques', bloques.map((item, i) => ({ ...item, orden: i })));
                            }} style={{ border: '1px solid #fda4af', color: '#be123c', background: '#fff', borderRadius: 8, padding: '6px 8px', cursor: 'pointer' }}>Eliminar</button>
                          </div>
                        </div>
                        <div className="form-grid">
                          <label className="field">
                            <span>Activo</span>
                            <input type="checkbox" checked={Boolean(bloque.activo)} onChange={(e) => {
                              const bloques = [...(empresaActual.landingPage?.bloques || [])];
                              bloques[index] = { ...bloques[index], activo: e.target.checked };
                              actualizarLanding('bloques', bloques);
                            }} />
                          </label>
                          <label className="field">
                            <span>Tipo</span>
                            <select value={bloque.tipo || 'texto'} onChange={(e) => {
                              const bloques = [...(empresaActual.landingPage?.bloques || [])];
                              bloques[index] = { ...bloques[index], tipo: e.target.value };
                              actualizarLanding('bloques', bloques);
                            }}>
                              <option value="texto">Texto</option>
                              <option value="html">HTML</option>
                              <option value="iframe">Iframe</option>
                              <option value="cta">CTA</option>
                            </select>
                          </label>
                          <label className="field span-2">
                            <span>Título</span>
                            <input value={bloque.titulo || ''} onChange={(e) => {
                              const bloques = [...(empresaActual.landingPage?.bloques || [])];
                              bloques[index] = { ...bloques[index], titulo: e.target.value };
                              actualizarLanding('bloques', bloques);
                            }} placeholder="Ej. Testimonios" />
                          </label>
                          <label className="field span-2">
                            <span>{bloque.tipo === 'iframe' ? 'URL del iframe' : bloque.tipo === 'cta' ? 'Enlace del botón' : 'Contenido'}</span>
                            <textarea rows={bloque.tipo === 'html' ? 6 : 3} value={bloque.contenido || ''} onChange={(e) => {
                              const bloques = [...(empresaActual.landingPage?.bloques || [])];
                              bloques[index] = { ...bloques[index], contenido: e.target.value };
                              actualizarLanding('bloques', bloques);
                            }} placeholder={bloque.tipo === 'cta' ? 'https://...' : bloque.tipo === 'iframe' ? 'https://...' : 'Texto o HTML según el tipo'} />
                          </label>
                          {bloque.tipo === 'iframe' ? (
                            <label className="field">
                              <span>Altura (px)</span>
                              <input type="number" min="120" max="2000" value={bloque.altura || 280} onChange={(e) => {
                                const bloques = [...(empresaActual.landingPage?.bloques || [])];
                                bloques[index] = { ...bloques[index], altura: Number(e.target.value) || 280 };
                                actualizarLanding('bloques', bloques);
                              }} />
                            </label>
                          ) : null}
                        </div>
                        <VistaPreviaBloque bloque={bloque} colorPrimario={empresaActual.landingPage?.colorPrimario || '#4f46e5'} />
                      </div>
                    ))}
                  </div>
                  <small className="muted">Puedes usar bloques de texto, HTML, iframe o CTA. Para iframes, usa URLs https.</small>
                </label>
              </div>

              <div style={{ display: 'grid', gap: 12 }}>
                <div style={{ border: '1px solid var(--border)', borderRadius: 14, padding: 12, background: '#f8fafc' }}>
                  <div className="muted" style={{ marginBottom: 10 }}>Plantillas rápidas</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    <button type="button" className="btn" onClick={() => aplicarPlantillaLanding('luxury')}>Luxury Brand</button>
                    <button type="button" className="btn" onClick={() => aplicarPlantillaLanding('clasico')}>Clásica</button>
                    <button type="button" className="btn" onClick={() => aplicarPlantillaLanding('moderno')}>Moderna</button>
                    <button type="button" className="btn" onClick={() => aplicarPlantillaLanding('minimalista')}>Minimalista</button>
                    <button type="button" className="btn" onClick={() => guardarPlantillaLandingActual()}>Guardar actual</button>
                    <button type="button" className="btn" onClick={() => setPreviewAmpliada((prev) => !prev)}>{previewAmpliada ? 'Cerrar vista ampliada' : 'Ampliar vista'}</button>
                  </div>
                  {Array.isArray(empresaActual?.landingPage?.templates) && empresaActual.landingPage.templates.length > 0 ? (
                    <div style={{ marginTop: 12, display: 'grid', gap: 8 }}>
                      {empresaActual.landingPage.templates.map((plantilla) => (
                        <div key={plantilla?.id || plantilla?.nombre} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, border: '1px solid var(--border)', borderRadius: 10, padding: '8px 10px', background: '#fff' }}>
                          <button type="button" className="btn btn-link" onClick={() => aplicarPlantillaGuardada(plantilla)} style={{ textAlign: 'left', flex: 1 }}>
                            {plantilla?.nombre || 'Plantilla guardada'}
                          </button>
                          <button type="button" className="btn btn-danger" onClick={() => eliminarPlantillaGuardada(plantilla?.id)} aria-label={`Eliminar ${plantilla?.nombre || 'plantilla'}`}>
                            Eliminar
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
                {previewAmpliada ? (
                  <div style={{ border: '1px solid var(--border)', borderRadius: 14, padding: 12, background: '#fff', position: 'sticky', top: 16 }}>
                    <VistaPreviaLandingVisual empresaActual={empresaActual} ampliada />
                  </div>
                ) : (
                  <VistaPreviaLandingVisual empresaActual={empresaActual} />
                )}
                <div style={{ marginTop: 0, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
                  {(empresaActual.landingPage?.logo || empresaActual.logo) ? (
                    <div>
                      <div className="muted" style={{ marginBottom: 6 }}>Preview logo</div>
                      <img src={empresaActual.landingPage?.logo || empresaActual.logo} alt="logo landing" style={{ width: 84, height: 84, borderRadius: 14, objectFit: 'cover', border: '1px solid var(--border)' }} />
                    </div>
                  ) : null}
                  {empresaActual.landingPage?.heroImage ? (
                    <div>
                      <div className="muted" style={{ marginBottom: 6 }}>Preview cabecera</div>
                      <img src={empresaActual.landingPage.heroImage} alt="cabecera landing" style={{ width: '100%', maxWidth: 320, height: 110, borderRadius: 12, objectFit: 'cover', border: '1px solid var(--border)' }} />
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </Seccion>
          ) : null}

          {tabActiva === 'equipo' ? (
          <Seccion title="Equipo" subtitle="Alta rapida de trabajadores y estado del personal" icon={<FiUsers />}>
            <div className="form-grid" style={{ marginBottom: 14 }}>
              <label className="field">
                <span>Nombre</span>
                <input value={trabajadorNuevo.nombre} onChange={(e) => setTrabajadorNuevo((p) => ({ ...p, nombre: e.target.value }))} />
              </label>
              <label className="field">
                <span>Apellidos</span>
                <input value={trabajadorNuevo.apellidos} onChange={(e) => setTrabajadorNuevo((p) => ({ ...p, apellidos: e.target.value }))} />
              </label>
              <label className="field">
                <span>Email</span>
                <input type="email" value={trabajadorNuevo.email} onChange={(e) => setTrabajadorNuevo((p) => ({ ...p, email: e.target.value }))} placeholder="empleado@empresa.com" />
              </label>
              <label className="field">
                <span>Contrasena inicial</span>
                <PasswordInput value={trabajadorNuevo.password} onChange={(e) => setTrabajadorNuevo((p) => ({ ...p, password: e.target.value }))} placeholder="minimo 6 caracteres" />
              </label>
              <label className="field">
                <span>Telefono</span>
                <input value={trabajadorNuevo.telefono} onChange={(e) => setTrabajadorNuevo((p) => ({ ...p, telefono: e.target.value }))} />
              </label>
              <label className="field">
                <span>Puesto</span>
                <input value={trabajadorNuevo.puesto} onChange={(e) => setTrabajadorNuevo((p) => ({ ...p, puesto: e.target.value }))} />
              </label>
              <label className="field">
                <span>Rol</span>
                <select value={trabajadorNuevo.rol} onChange={(e) => setTrabajadorNuevo((p) => ({ ...p, rol: e.target.value }))}>
                  <option value="empleado">Empleado</option>
                  {esSuperadmin ? <option value="admin">Administrador</option> : null}
                </select>
              </label>
            </div>
            <button className="btn btn-primary" onClick={crearTrabajador} disabled={creandoTrabajador}>
              {creandoTrabajador ? 'Registrando...' : 'Registrar trabajador'}
            </button>

            <div style={{ marginTop: 16, overflowX: 'auto' }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>Email</th>
                    <th>Rol</th>
                    <th>Puesto</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {trabajadores.map((item) => (
                    <tr key={item._id}>
                      <td>{[item.nombre, item.apellidos].filter(Boolean).join(' ') || 'Sin nombre'}</td>
                      <td>{item.email}</td>
                      <td>{item.rol}</td>
                      <td>{item.puesto || '-'}</td>
                      <td>
                        <span className={`badge ${item.activo === false ? 'badge-warning' : 'badge-ok'}`}>
                          {item.activo === false ? 'Inactivo' : 'Activo'}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {trabajadores.length === 0 ? (
                    <tr>
                      <td colSpan="5" style={{ textAlign: 'center', color: 'var(--text-soft)' }}>No hay trabajadores todavia.</td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </Seccion>
          ) : null}

          {tabActiva === 'comunicaciones' ? (
          <Seccion title="Comunicaciones" subtitle="Slack y SMTP" icon={<FiMail />}>
            <div className="form-grid">
              <label className="field span-2">
                <span>Webhook de Slack</span>
                {esSuperadmin ? <input value={configSlack.webhookUrl || ''} onChange={(e) => actualizarConfigSlack({ webhookUrl: e.target.value })} placeholder="https://hooks.slack.com/services/..." /> : <small className="muted">{empresaActual.slackConfigurado ? 'Webhook configurado por el superadmin.' : 'Webhook no configurado.'}</small>}
              </label>
              <label className="field span-2" style={{ alignItems: 'flex-start' }}>
                <span>Activar alertas por Slack</span>
                <label className="field checkbox" style={{ justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                  <span>Enviar notificaciones a Slack cuando estén habilitadas</span>
                  <input type="checkbox" checked={Boolean(configSlack.activo)} onChange={(e) => actualizarConfigSlack({ activo: e.target.checked })} />
                </label>
              </label>
              <label className="field span-2">
                <span>Canal Slack (opcional)</span>
                <input value={configSlack.canal || ''} onChange={(e) => actualizarConfigSlack({ canal: e.target.value })} placeholder="#alertas-trix" />
              </label>
              <label className="field span-2">
                <span>Eventos a notificar</span>
                <div style={{ display: 'grid', gap: 8 }}>
                  {Object.entries(configSlack.catalogo || {}).map(([clave, meta]) => (
                    <label key={clave} className="field checkbox" style={{ justifyContent: 'space-between', alignItems: 'center', border: '1px solid var(--border)', borderRadius: 10, padding: '10px 12px' }}>
                      <span>{meta.etiqueta || clave}</span>
                      <input
                        type="checkbox"
                        checked={Boolean(configSlack.eventos?.[clave])}
                        onChange={(e) => actualizarConfigSlack((prev) => ({
                          ...prev,
                          eventos: { ...(prev.eventos || {}), [clave]: e.target.checked },
                        }))}
                      />
                    </label>
                  ))}
                </div>
              </label>
              <label className="field span-2">
                <span>Plantilla Slack alta trabajador (variables: {'{{empresa}}'}, {'{{usuario}}'}, {'{{email}}'}, {'{{rol}}'}, {'{{actor}}'})</span>
                <textarea
                  rows={4}
                  value={empresaActual.slackPlantillaAltaTrabajador || ''}
                  onChange={(e) => actualizarEmpresaActual('slackPlantillaAltaTrabajador', e.target.value)}
                  placeholder={PLANTILLA_SLACK_POR_DEFECTO}
                />
              </label>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: -6, marginBottom: 6 }}>
                <button type="button" className="btn" onClick={() => actualizarEmpresaActual('slackPlantillaAltaTrabajador', PLANTILLA_SLACK_POR_DEFECTO)}>
                  Restablecer plantilla por defecto
                </button>
                <span className="muted">{String(empresaActual.slackPlantillaAltaTrabajador || '').length}/{LIMITES.plantillaSlack} caracteres</span>
              </div>
              <label className="field span-2">
                <span>Vista previa del mensaje Slack</span>
                <textarea rows={6} value={previewSlackAlta} readOnly />
              </label>
              {esSuperadmin ? (
                <label className="field span-2">
                  <span>Canal por defecto</span>
                  <input value={empresaActual.slackCanal || ''} onChange={(e) => actualizarEmpresaActual('slackCanal', e.target.value)} placeholder="#alertas-trix" />
                </label>
              ) : null}
              {esSuperadmin ? (
                <>
                  <label className="field">
                    <span>Correo saliente</span>
                    <input type="email" value={empresaActual.correoSaliente || ''} onChange={(e) => actualizarEmpresaActual('correoSaliente', e.target.value)} />
                  </label>
                  <label className="field">
                    <span>Contrasena SMTP</span>
                    <PasswordInput value={empresaActual.correoSalientePassword || ''} onChange={(e) => actualizarEmpresaActual('correoSalientePassword', e.target.value)} />
                  </label>
                  <label className="field">
                    <span>Host SMTP</span>
                    <input value={empresaActual.smtpHost || ''} onChange={(e) => actualizarEmpresaActual('smtpHost', e.target.value)} placeholder="smtp.gmail.com" />
                  </label>
                  <label className="field">
                    <span>Puerto SMTP</span>
                    <input type="number" min="1" max="65535" value={empresaActual.smtpPort ?? ''} onChange={(e) => actualizarEmpresaActual('smtpPort', e.target.value === '' ? null : Number(e.target.value))} placeholder="465" />
                  </label>
                  <label className="field" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span>SSL/TLS</span>
                    <input type="checkbox" checked={Boolean(empresaActual.smtpSecure)} onChange={(e) => actualizarEmpresaActual('smtpSecure', e.target.checked)} />
                  </label>
                  <label className="field span-2">
                    <span>Email destino para prueba SMTP</span>
                    <input value={correoPruebaDestino} onChange={(e) => setCorreoPruebaDestino(e.target.value)} placeholder="destino@empresa.com" />
                  </label>
                </>
              ) : null}
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
              <button className="btn" onClick={probarSlack} disabled={probandoSlack}>
                {probandoSlack ? 'Probando Slack...' : 'Probar Slack'}
              </button>
              {esSuperadmin ? (
                <button className="btn" onClick={probarCorreo} disabled={probandoCorreo}>
                  {probandoCorreo ? 'Probando SMTP...' : 'Probar SMTP'}
                </button>
              ) : null}
            </div>
          </Seccion>
          ) : null}

          {(esSuperadmin || esAdminEmpresa) && tabActiva === 'ia' ? (
            <Seccion title="Cuotas de IA" subtitle="Consumo mensual de tokens de la empresa y de cada usuario" icon={<FiShield />}>
              {errorCuotasIA ? <div className="alert-error">{errorCuotasIA}</div> : null}
              {mensajeCuotasIA ? <div className="alert-warning">{mensajeCuotasIA}</div> : null}
              {cargandoCuotasIA && !cuotasIA ? <div className="muted">Cargando cuotas de IA...</div> : null}
              {cuotasIA ? (
                <div style={{ display: 'grid', gap: 16 }}>
                  <div className="table-wrap" style={{ padding: 14, display: 'grid', gap: 10 }}>
                    <div className="muted">Cuota de IA de la empresa</div>
                    <div className="form-grid">
                      <label className="field">
                        <span>Límite mensual de tokens (0 = ilimitado)</span>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={formCuotaEmpresa.limiteTokensMensual}
                          disabled={!esSuperadmin}
                          onChange={(e) => setFormCuotaEmpresa((prev) => ({ ...prev, limiteTokensMensual: e.target.value }))}
                        />
                      </label>
                      <label className="field checkbox">
                        <span>IA activa para la empresa</span>
                        <input
                          type="checkbox"
                          checked={Boolean(formCuotaEmpresa.activa)}
                          disabled={!esSuperadmin}
                          onChange={(e) => setFormCuotaEmpresa((prev) => ({ ...prev, activa: e.target.checked }))}
                        />
                      </label>
                      <label className="field">
                        <span>Consumido este mes</span>
                        <input value={`${Number(cuotasIA.empresa?.tokensConsumidosMes) || 0} tokens`} readOnly />
                      </label>
                      <label className="field">
                        <span>Periodo de uso</span>
                        <input value={cuotasIA.empresa?.periodoUso || 'Sin periodo'} readOnly />
                      </label>
                    </div>
                    {Number(cuotasIA.empresa?.limiteTokensMensual) > 0 ? (
                      (() => {
                        const limite = Number(cuotasIA.empresa.limiteTokensMensual);
                        const consumido = Number(cuotasIA.empresa?.tokensConsumidosMes) || 0;
                        const porcentaje = Math.min(100, Math.round((consumido / limite) * 100));
                        return (
                          <div>
                            <div style={{ height: 10, borderRadius: 6, background: 'rgba(148,163,184,0.25)', overflow: 'hidden' }}>
                              <div style={{ width: `${porcentaje}%`, height: '100%', background: porcentaje >= 100 ? '#be123c' : porcentaje >= 80 ? '#f59e0b' : '#22c55e' }} />
                            </div>
                            <div className="muted" style={{ marginTop: 6 }}>{consumido} / {limite} tokens ({porcentaje}%)</div>
                          </div>
                        );
                      })()
                    ) : (
                      <div className="muted">Sin límite: la empresa tiene consumo ilimitado de tokens.</div>
                    )}
                    {esSuperadmin ? (
                      <div>
                        <button type="button" className="btn btn-primary" onClick={guardarCuotaEmpresaIA} disabled={guardandoCuotaEmpresa}>
                          {guardandoCuotaEmpresa ? 'Guardando...' : 'Guardar cuota de empresa'}
                        </button>
                      </div>
                    ) : (
                      <div className="muted">La cuota de la empresa solo puede modificarla el superadministrador.</div>
                    )}
                  </div>

                  <div className="table-wrap" style={{ padding: 14, display: 'grid', gap: 10 }}>
                    <div className="muted">Límites por usuario</div>
                    <div className="alert-warning">
                      Un límite de 0 significa <strong>sin límite propio</strong>: ese usuario se valida únicamente contra la cuota de la empresa.
                      El límite del usuario nunca amplía la cuota de la empresa: si la cuota de empresa está agotada, se bloquea aunque al usuario le quede margen.
                    </div>
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Nombre</th>
                          <th>Email</th>
                          <th>Rol</th>
                          <th>Consumido este mes</th>
                          <th>Límite mensual</th>
                          <th>Acción</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(cuotasIA.usuarios || []).map((u) => {
                          const limite = Number(u.limiteTokensMensual) || 0;
                          const consumido = Number(u.tokensConsumidosMes) || 0;
                          const superado = limite > 0 && consumido >= limite;
                          return (
                            <tr key={u._id}>
                              <td>{[u.nombre, u.apellidos].filter(Boolean).join(' ') || 'Sin nombre'}</td>
                              <td>{u.email || '-'}</td>
                              <td>{u.rol || '-'}</td>
                              <td style={superado ? { color: '#be123c', fontWeight: 600 } : undefined}>
                                {consumido} tokens{superado ? ' · límite alcanzado' : ''}
                              </td>
                              <td>
                                <input
                                  type="number"
                                  min="0"
                                  step="1"
                                  placeholder="0 = sin límite"
                                  value={limitesUsuarioIA[u._id] ?? ''}
                                  onChange={(e) => setLimitesUsuarioIA((prev) => ({ ...prev, [u._id]: e.target.value }))}
                                />
                                <div className="muted" style={{ fontSize: 12 }}>{limite > 0 ? `Actual: ${limite} tokens` : 'Actual: Sin límite'}</div>
                              </td>
                              <td>
                                <button type="button" className="btn" onClick={() => guardarCuotaUsuarioIA(u)} disabled={guardandoCuotaUsuario === u._id}>
                                  {guardandoCuotaUsuario === u._id ? 'Guardando...' : 'Guardar'}
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                        {(cuotasIA.usuarios || []).length === 0 ? (
                          <tr><td colSpan={6} className="muted">No hay usuarios en esta empresa.</td></tr>
                        ) : null}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null}
            </Seccion>
          ) : null}

          {esSuperadmin && tabActiva === 'ia' ? (
            <Seccion
              title="Prompts del Sistema por Uso"
              subtitle="Dinamiza las instrucciones que recibe la IA en cada módulo sin modificar código"
              icon={<FiCpu />}
            >
              <div className="alert-info" style={{ marginBottom: 16 }}>
                Puedes personalizar los System Prompts para adaptar el comportamiento de la IA al tipo de negocio de esta empresa. Si dejas un prompt vacío, Trix aplicará el prompt recomendado por defecto.
              </div>

              <div style={{ display: 'grid', gap: 16 }}>
                {[
                  {
                    uso: 'presupuestos',
                    titulo: '📋 Generación de Presupuestos',
                    descripcion: 'Instrucciones para extraer mano de obra, materiales, unidades y precios desde notas de voz o textos.',
                  },
                  {
                    uso: 'almacen',
                    titulo: '📦 Importación de Almacén',
                    descripcion: 'Instrucciones para extraer catálogo e inventario desde facturas, albaranes, imágenes o voz.',
                  },
                  {
                    uso: 'clientes',
                    titulo: '👤 Extracción de Clientes',
                    descripcion: 'Instrucciones para extraer fichas de cliente (NIF, teléfono, email, CRM) desde audio o texto.',
                  },
                  {
                    uso: 'presupuestos_faltantes',
                    titulo: '🔍 Revisión de Faltantes en Presupuesto',
                    descripcion: 'Instrucciones para analizar presupuestos y sugerir partidas o materiales omitidos en obras/servicios.',
                  },
                  {
                    uso: 'tareas',
                    titulo: '✅ Generación de Tareas',
                    descripcion: 'Instrucciones para transformar notas de voz o texto en tareas con prioridad, asignación y fecha estimada.',
                  },
                ].map((item) => {
                  const promptActual = empresaActual.ia?.[item.uso]?.prompt;
                  const usandoDefecto = !promptActual || !promptActual.trim();

                  return (
                    <div key={item.uso} className="table-wrap" style={{ padding: 16, display: 'grid', gap: 10 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                        <div>
                          <strong>{item.titulo}</strong>
                          <div className="muted" style={{ fontSize: 13 }}>{item.descripcion}</div>
                        </div>
                        <span className={`badge ${usandoDefecto ? '' : 'badge-info'}`}>
                          {usandoDefecto ? 'Prompt por defecto' : 'Prompt personalizado'}
                        </span>
                      </div>

                      <label className="field" style={{ margin: 0 }}>
                        <span>System Prompt personalizado ({item.uso})</span>
                        <textarea
                          rows={3}
                          value={promptActual ?? ''}
                          placeholder={PROMPTS_POR_DEFECTO[item.uso]}
                          onChange={(e) => actualizarPromptUsoIA(item.uso, e.target.value)}
                        />
                      </label>

                      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', alignItems: 'center' }}>
                        <button
                          type="button"
                          className="btn mini"
                          onClick={() => actualizarPromptUsoIA(item.uso, PROMPTS_POR_DEFECTO[item.uso])}
                        >
                          Restablecer a prompt recomendado
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Seccion>
          ) : null}

          {esSuperadmin && tabActiva === 'ia' ? (
            <Seccion title="IAs de la empresa" subtitle="Se prueban de menor a mayor orden hasta obtener respuesta" icon={<FiShield />}>
              <div className="alert-warning">Configura una IA por fila. Las filas activas se intentan en orden ascendente; si una falla, Trix pasa automáticamente a la siguiente.</div>
              <div style={{ display: 'grid', gap: 12 }}>
                <div className="table-wrap" style={{ padding: 14, display: 'grid', gap: 10 }}>
                  <div className="muted">Diagnóstico rápido de Groq</div>
                  <label className="field">
                    <span>API key de Groq para explorar modelos</span>
                    <PasswordInput value={apiKeyExploracionGroq} onChange={(e) => setApiKeyExploracionGroq(e.target.value)} placeholder="gsk_..." autoComplete="new-password" />
                  </label>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                    <button type="button" className="btn" onClick={explorarModelosGroq} disabled={explorandoModelosGroq}>
                      {explorandoModelosGroq ? 'Explorando...' : 'Explorar modelos'}
                    </button>
                  </div>
                  {resultadosExploracionGroq ? <pre style={{ whiteSpace: 'pre-wrap', margin: 0, fontSize: 12 }}>{resultadosExploracionGroq}</pre> : null}
                </div>
                {(empresaActual.ia?.catalogo || []).slice().sort((a, b) => (Number(a.orden) || 1) - (Number(b.orden) || 1)).map((ia, indiceOrden) => {
                  const indice = (empresaActual.ia?.catalogo || []).findIndex((item) => item === ia);
                  return (
                    <div key={ia._id || `${ia.clave}-${indice}`} className="table-wrap" style={{ padding: 14 }}>
                      <div className="form-grid">
                        <label className="field"><span>Orden</span><input type="number" min="1" value={ia.orden || indiceOrden + 1} onChange={(e) => actualizarIA(indice, 'orden', Number(e.target.value) || 1)} /></label>
                        <label className="field"><span>Reintentos</span><input type="number" min="0" max="5" step="1" value={normalizarReintentosIA(ia.reintentos)} onChange={(e) => actualizarIA(indice, 'reintentos', normalizarReintentosIA(e.target.value))} /></label>
                        <label className="field">
                          <span>Uso</span>
                          <select value={ia.clave || 'presupuestos'} onChange={(e) => actualizarIA(indice, 'clave', e.target.value)}>
                            <option value="presupuestos">Presupuestos</option>
                            <option value="almacen">Almacén</option>
                            <option value="clientes">Clientes</option>
                            <option value="presupuestos_faltantes">Revisión y Faltantes</option>
                            <option value="tareas">Tareas</option>
                          </select>
                        </label>
                        <label className="field"><span>Nombre</span><input value={ia.nombre || ''} onChange={(e) => actualizarIA(indice, 'nombre', e.target.value)} placeholder="IA principal" /></label>
                        <label className="field"><span>Proveedor</span><select value={ia.proveedor || 'groq'} onChange={(e) => actualizarIA(indice, 'proveedor', e.target.value)}><option value="groq">Groq</option><option value="gemini">Gemini</option><option value="openrouter">OpenRouter</option><option value="local">Local (Ollama)</option></select></label>
                        <label className="field"><span>Modelo chat</span><input value={ia.modeloChat || ''} onChange={(e) => actualizarIA(indice, 'modeloChat', e.target.value)} placeholder="llama3.1:8b" /></label>
                        <label className="field"><span>Modelo transcripcion</span><input value={ia.modeloTranscripcion || ''} onChange={(e) => actualizarIA(indice, 'modeloTranscripcion', e.target.value)} placeholder="whisper-small" /></label>
                        <label className="field span-2"><span>API key {ia.proveedor === 'gemini' ? 'Gemini' : ia.proveedor === 'openrouter' ? 'OpenRouter' : 'Groq'} de esta IA</span><PasswordInput value={ia.apiKey || ''} onChange={(e) => actualizarIA(indice, 'apiKey', e.target.value)} placeholder={ia.apiKeyConfigurada ? 'Configurada. Escribe una nueva para reemplazarla.' : ia.proveedor === 'local' ? 'No necesaria para Ollama' : `Solo para proveedor ${ia.proveedor === 'gemini' ? 'Gemini' : ia.proveedor === 'openrouter' ? 'OpenRouter' : 'Groq'}`} autoComplete="new-password" /></label>
                        <label className="field span-2">
                          <span>Prompt del sistema específico para este proveedor/fila (opcional)</span>
                          <textarea
                            rows={2}
                            value={ia.prompt || ''}
                            onChange={(e) => actualizarIA(indice, 'prompt', e.target.value)}
                            placeholder="Si se deja vacío, usará el prompt del sistema configurado arriba o el recomendado."
                          />
                        </label>
                        <label className="field checkbox"><span>IA activa</span><input type="checkbox" checked={ia.activo !== false} onChange={(e) => actualizarIA(indice, 'activo', e.target.checked)} /></label>
                      </div>
                      <div style={{ display: 'grid', gap: 10, marginTop: 12 }}>
                        <label className="field span-2">
                          <span>Prompt de prueba</span>
                          <input
                            value={empresaActual?._id ? (promptsPruebaIA[empresaActual._id] || promptPruebaIA) : promptPruebaIA}
                            onChange={(e) => {
                              const valor = e.target.value;
                              setPromptPruebaIA(valor);
                              if (empresaActual?._id) {
                                setPromptsPruebaIA((prev) => ({ ...prev, [empresaActual._id]: valor }));
                              }
                            }}
                            placeholder="Escribe el texto a probar, por ejemplo: hola"
                          />
                        </label>
                        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                          <label className="field" style={{ minWidth: 180, margin: 0 }}>
                            <span>Modo de prueba</span>
                            <select value={modoPruebaIA || 'strict'} onChange={(e) => setModoPruebaIA(e.target.value)}>
                              <option value="strict">Estricto (solo la fila)</option>
                              <option value="fallback">Con fallback</option>
                            </select>
                          </label>
                          <button type="button" className="btn" onClick={() => probarIAEmpresa(ia)} disabled={probandoIA}>
                            {probandoIA ? 'Probando...' : 'Probar IA'}
                          </button>
                          {esSuperadmin ? (
                            <button type="button" className="btn" style={{ borderColor: '#fda4af', color: '#be123c' }} onClick={() => eliminarIA(ia)}>
                              Eliminar IA
                            </button>
                          ) : null}
                          {resultadoPruebaIA ? <span className="muted" style={{ whiteSpace: 'pre-wrap' }}>{resultadoPruebaIA}</span> : null}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <button type="button" className="btn btn-primary" style={{ marginTop: 14 }} onClick={anadirIA}>Añadir IA</button>
            </Seccion>
          ) : null}

          {esSuperadmin && tabActiva === 'permisos' ? (
            <Seccion title="Modulos y feature flags" subtitle="Control avanzado por empresa" icon={<FiShield />}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <div className="muted" style={{ marginBottom: 10 }}>Modulos</div>
                  <div style={{ display: 'grid', gap: 8 }}>
                    {modulos.map((modulo) => (
                      <label key={modulo.clave} className="field checkbox" style={{ justifyContent: 'space-between', border: '1px solid var(--border)', borderRadius: 10, padding: '10px 12px' }}>
                        <span>{modulo.nombre}</span>
                        <input type="checkbox" checked={Boolean((empresaActual.modulos || {})[modulo.clave])} onChange={() => toggleModulo(modulo.clave)} />
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="muted" style={{ marginBottom: 10 }}>Feature flags</div>
                  <div style={{ display: 'grid', gap: 8 }}>
                    {featureEntries.map(([clave, cfg]) => (
                      <label key={clave} className="field checkbox" style={{ justifyContent: 'space-between', border: '1px solid var(--border)', borderRadius: 10, padding: '10px 12px' }}>
                        <span>{cfg.nombre}</span>
                        <input type="checkbox" checked={Boolean((empresaActual.featureFlags || {})[clave])} onChange={() => toggleFeature(clave)} />
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </Seccion>
          ) : null}

            {tabActiva === 'empresa' ? (
              <Seccion title="Historial de cambios" subtitle="Quien cambio que y cuando" icon={<FiCheckCircle />}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'end', flexWrap: 'wrap', marginBottom: 14 }}>
                  <label className="field">
                    <span>Desde</span>
                    <input type="date" value={historialDesde} onChange={(e) => setHistorialDesde(e.target.value)} />
                  </label>
                  <label className="field">
                    <span>Hasta</span>
                    <input type="date" value={historialHasta} onChange={(e) => setHistorialHasta(e.target.value)} />
                  </label>
                  <button type="button" className="btn" onClick={aplicarFiltroHistorial}>Filtrar historial</button>
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Fecha</th>
                        <th>Usuario</th>
                        <th>Rol</th>
                        <th>Cambios</th>
                      </tr>
                    </thead>
                    <tbody>
                      {historialAjustes.map((item, idx) => (
                        <tr key={`${item?.fecha || 'sin-fecha'}-${idx}`}>
                          <td>{item?.fecha ? new Date(item.fecha).toLocaleString('es-ES') : '-'}</td>
                          <td>{item?.usuarioNombre || 'Sistema'}</td>
                          <td>{item?.rol || '-'}</td>
                          <td>
                            {Array.isArray(item?.cambios) && item.cambios.length > 0
                              ? item.cambios.map((cambio) => cambio?.campo).filter(Boolean).join(', ')
                              : '-'}
                          </td>
                        </tr>
                      ))}
                      {historialAjustes.length === 0 ? (
                        <tr>
                          <td colSpan="4" style={{ textAlign: 'center', color: 'var(--text-soft)' }}>No hay cambios registrados todavia.</td>
                        </tr>
                      ) : null}
                    </tbody>
                  </table>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginTop: 12, flexWrap: 'wrap' }}>
                  <span className="muted">{historialPaginacion.total || 0} cambios encontrados</span>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <button type="button" className="btn" onClick={() => cambiarPaginaHistorial(historialPagina - 1)} disabled={historialPagina <= 1}>Anterior</button>
                    <span className="muted">Pagina {historialPagina} de {historialPaginacion.paginas || 1}</span>
                    <button type="button" className="btn" onClick={() => cambiarPaginaHistorial(historialPagina + 1)} disabled={historialPagina >= (historialPaginacion.paginas || 1)}>Siguiente</button>
                  </div>
                </div>
              </Seccion>
            ) : null}
        </>
      ) : (
        <div className="alert-warning">Selecciona una empresa para continuar.</div>
      )}
    </div>
  );
}
